import axios, { AxiosError } from "axios";
import {
  AdminFileManagementResponse,
  BrainBankExport,
  BrainBankExportResult,
  ChatBrainBankSyncResult,
  SessionFileBrainBankSyncs,
  BrainBankSyncSummary,
  ImportedTranscript,
  ImportedTranscriptMeta,
  PaginationInfo,
  CreateKnowledgeBaseRequest,
  FileItem,
  GetUsersResponse,
  LicenseInfo,
  Model,
  UpdateKnowledgeBaseRequest,
  UnverifiedUser,
  User,
  UserFileManagementResponse,
  FeedbackRequest,
  FeedbackResponse,
  GuardrailSettings,
  GuardrailSettingsResponse,
  Notification,
  NotificationResponse,
  UnreadCountResponse,
  NotificationActionResponse,
  MeetingSession,
  MeetingSessionListResponse,
  CreateMeetingSessionResponse,
  ChunkUploadResponse,
  UploadStatusResponse,
  FinalizeResponse,
  TranscriptionStatusResponse,
  Transcription,
  SpeakerMapping,
  VAModelSuggestion,
  LockResourceType,
  LockFileType,
  LockStateResponse,
  CreateDropzoneLinkRequest,
  CreatedDropzoneLink,
  DropzoneLink,
  HeldDropzoneFile,
} from "../types";

/**
* IMPORTANT FOR DEPLOYMENT:
* 
* Before pushing to repository:
* 1. UNCOMMENT these lines for pipeline builds
*    and COMMENT OUT all other endpoint configurations
*/
const endpoint = process.env.REACT_APP_API_ENDPOINT;
export const websocket_endpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT;
export const websocket_voice_endpoint = process.env.REACT_APP_WEBSOCKET_VOICE_ENDPOINT;
// Live meeting transcript stream (WS-01). Absent → the live panel is
// unavailable and recording behaves exactly as before the feature.
export const websocket_transcript_endpoint = process.env.REACT_APP_WEBSOCKET_TRANSCRIPT_ENDPOINT;

/**
* DO NOT PUSH WITH THESE CONFIGURATIONS UNCOMMENTED
* 
* 
* Staging environment endpoints - for local testinbut g only
*/
// const endpoint = "https://api-qa.grapes.cloud";
// export const websocket_endpoint = "wss://api-qa.grapes.cloud/conversation/"
// export const websocket_voice_endpoint = "wss://api-qa.grapes.cloud/voice-conversation/"

/**
* DO NOT PUSH WITH THESE CONFIGURATIONS UNCOMMENTED
* Local development endpoints - for local testing only
*/
// const endpoint = "http://127.0.0.1:8007";
// export const websocket_endpoint = "ws://localhost:8007/conversation/";
// export const websocket_voice_endpoint = "ws://localhost:8007/voice-conversation/";

const api = axios.create({
  baseURL: endpoint,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Track re-auth status and queue requests
let isReAuthenticating = false;
let reAuthPromise: Promise<any> | null = null;
let requestQueue: (() => void)[] = [];

let reAuthenticateFunc: (() => Promise<{ success: boolean; token?: string }>) | null = null;

export const setReAuthFunction = (
  func: () => Promise<{ success: boolean; token?: string }>
) => {
  reAuthenticateFunc = func;
};

export const setAuthToken = (token: string) => {
  sessionStorage.setItem("authToken", token);
  api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

const isTokenExpired = () => {
  const expTime = sessionStorage.getItem("authTokenExp");
  if (!expTime) return false;

  const expTimestamp = parseInt(expTime) * 1000;
  const currentTime = Date.now();

  // 10 seconds buffer
  return currentTime >= expTimestamp - 10000;
};

// 🔥 Handle re-authentication properly with queuing
const handleReAuthentication = async () => {
  if (!isReAuthenticating && reAuthenticateFunc) {
    isReAuthenticating = true;

    try {
      reAuthPromise = reAuthenticateFunc();
      const result = await reAuthPromise;

      if (result.success && result.token) {
        console.log("Re-authentication successful");

        // ✅ Update token in header for all subsequent requests
        setAuthToken(result.token);

        // ✅ Resolve all queued requests
        requestQueue.forEach((resolve) => resolve());
        requestQueue = [];
      } else {
        console.error("Re-authentication failed");
      }
    } catch (error) {
      console.error("Error during re-authentication:", error);
    } finally {
      isReAuthenticating = false;
      reAuthPromise = null;
    }
  }

  // Wait for re-authentication to complete
  return reAuthPromise;
};

// 🔥 Axios interceptor with queuing logic
api.interceptors.request.use(
  async (config) => {
    if (isTokenExpired() && reAuthenticateFunc) {
      if (isReAuthenticating) {
        console.log("Re-auth in progress, queuing request...");

        // ✅ Queue the request and wait for the new token
        await new Promise<void>((resolve) => requestQueue.push(() => resolve()));
      } else {
        console.log("Token expired, starting re-authentication...");
        await handleReAuthentication();
      }
    }

    // Ensure the latest token is used
    const token = sessionStorage.getItem("authToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Raw WebSockets bypass the axios interceptor, so socket openers call
// this to get a token that is guaranteed fresh (runs the same queued
// re-auth flow the interceptor uses).
export const ensureFreshAuthToken = async (): Promise<string | null> => {
  if (isTokenExpired() && reAuthenticateFunc) {
    await handleReAuthentication();
  }
  return sessionStorage.getItem("authToken");
};

export const createSession = async (
  modelId?: string,
  hatId?: string,
  title?: string
) => {
  try {
    const payload = {
      model_id: modelId,
      hat_id: hatId,
      title: title,
    };

    const response = await api.post("/session/", payload);
    console.log(`Session created: `, response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating session:", error);
    throw error;
  }
};



export const deleteSession = async (session_id: string | undefined) => {
  if (!session_id) {
    console.error("Session ID is required");
    return;
  }

  try {
    const response = await api.delete(`/session/${session_id}`);
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting session:", error);
  }
};

export const loadSessions = async () => {
  try {
    const response = await api.get(`/session/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sessions:", error);
  }
};

export const loadConversation = async (session_id: string): Promise<any[]> => {
  try {
    const response = await api.get(`/messages/${session_id}`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching session messages:", error);

    if (error.isAxiosError) {
      const axiosError = error as AxiosError;

      if (axiosError.response && axiosError.response.status === 404) {
        console.warn(`No conversation found for sessionId: ${session_id}`);
        throw new Error("No conversation found");
      }
    }

    console.error("An unexpected error occurred:", error);
    throw error;
  }
};

export const updateSessionTitleById = async (session_id: string, force: boolean = false) => {
  try {
    // force=true tells the backend to regenerate even if a title already exists — used for
    // the second pass once the model's answer is saved, so the title reflects the full exchange.
    const response = await api.put(`/session/${session_id}/title${force ? "?force=true" : ""}`);
    console.log("Success:", response.data);
  } catch (error) {
    console.error("Error updating session title:", error);
  }
};

// User Management
export const createOrUpdateCurrentUser = async () => {

  try {
    const response = await api.post(`/user/`);
    console.log("Current user created or updated:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error(error.response.data?.detail || 'Your account has been disabled. Please contact Kyva administrator.');
    }
    throw new Error(((error as any).response?.data?.detail) || 'Failed to create user');
  }
};

export const generateFeedbackSummary = async (conversation_context: string) => {
  try {
    const response = await api.post("/feedback/generate-summary", {
      conversation_context
    });
    return response.data;
  } catch (error) {
    console.error("Error generating feedback summary:", error);
    return null;
  }
};

export const disableUser = async (
  user_id: string
): Promise<any> => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.put(`/user/${user_id}`, {
      user_status: "disabled"
    });
    console.log("User disabled:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error disabling user:", error);
    throw error;
  }
};

export const getAllUsersIncludingArchived = async (): Promise<User[]> => {
  try {
    const response = await api.get(`/user/all-including-archived`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching all users including archived:", error);
    throw error;
  }
};

export const archiveUser = async (
  user_id: string
): Promise<any> => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.post(`/user/${user_id}/archive`);
    console.log("User archived:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error archiving user:", error);
    throw error;
  }
};

export const restoreUser = async (
  user_id: string
): Promise<any> => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.post(`/user/${user_id}/restore`);
    console.log("User restored:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error restoring user:", error);
    throw error;
  }
};

export const getUnverifiedUsers = async (): Promise<UnverifiedUser[]> => {
  try {
    const response = await api.get(`/user/unverified`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching unverified users:", error);
    throw error;
  }
};

export const deleteUnverifiedUser = async (
  user_id: string
): Promise<any> => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.delete(`/user/${user_id}/unverified`);
    console.log("Unverified user deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting unverified user:", error);
    throw error;
  }
};

export const createUser = async (user_id: string, role: string | undefined, permissions?: any) => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    // First create the user with default permissions
    const response = await api.post(`/user/create`, { user_id, role });
    console.log("User created:", response.data);

    // If custom permissions are provided, update the user with those permissions
    if (permissions) {
      await updateUser(user_id, role, "enabled", permissions);
      console.log("User permissions updated:", permissions);
    }

    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const updateUser = async (user_id: string, role: string | undefined, user_status: string, permissions?: any) => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const payload: any = { role, user_status };
    if (permissions) {
      payload.permissions = permissions;
    }
    const response = await api.put(`/user/${user_id}`, payload);
    console.log("User Updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
  }
};

export const assignKnowledgeBaseToUser = async (
  user_id: string,
  assigned_knowledge_base_ids: string[]
) => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.put(`/user/${user_id}/knowledge-base/assign`, {
      knowledge_base_ids: assigned_knowledge_base_ids,
    });
    console.log("Knowledge base assigned to user:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error assigning knowledge base to user:", error);
  }
};

export const unassignKnowledgeBaseFromUser = async (
  user_id: string,
  knowledge_base_ids: string[]
) => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.put(`/user/${user_id}/knowledge-base/unassign`, {
      knowledge_base_ids: knowledge_base_ids,
    });
    console.log("Knowledge base unassigned from user:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error unassigning knowledge base from user:", error);
  }
};

export const getUsers = async (): Promise<User[]> => {
  try {
    const response = await api.get(`/user/`);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

export const getMyDetails = async () => {
  try {
    const response = await api.get(`/user/me`);
    console.log("User retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving user:", error);
    throw error;
  }
};

export const getUsersForPaginated = async (
  page: number,
  itemsPerPage: number
): Promise<GetUsersResponse> => {
  try {
    const response = await api.get("/user/paginated-users", {
      params: {
        page,
        items_per_page: itemsPerPage,
      },
    });

    console.log(response.data);

    return {
      users: response.data.users,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error("Error fetching paginated users:", error);
    throw error;
  }
};

export const getTokenUsageForPaginatedUsers = async (
  page = 1,
  itemsPerPage = 10,
  timePeriod = "monthly",
  month?: string,
  startDate?: string,
  endDate?: string
) => {
  try {
    if (month) {
      const monthRegex = /^[0-9]{4}-(?:0[1-9]|1[0-2])$/;
      if (!monthRegex.test(month)) {
        throw new Error("Invalid month format. Please use YYYY-MM format.");
      }
    }

    const params: any = {
      page,
      items_per_page: itemsPerPage,
      time_period: timePeriod,
    };

    if (timePeriod === "monthly" && month) {
      params.month = month;
    }

    if (timePeriod === "daily") {
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    }

    const response = await api.get("/token-usage/paginated-users", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching token usage for paginated users:", error);
    throw error;
  }
};

export const getTokenUsageCoverage = async (month?: string) => {
  try {
    if (month) {
      const monthRegex = /^[0-9]{4}-(?:0[1-9]|1[0-2])$/;
      if (!monthRegex.test(month)) {
        throw new Error("Invalid month format. Please use YYYY-MM format.");
      }
    }

    const response = await api.get("/token-usage/coverage", {
      params: month ? { month } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching token usage coverage:", error);
    throw error;
  }
};

export const getTokenUsagePresignedUrl = async (
  month?: string,
  userIds?: string[]
) => {
  try {
    if (month) {
      const monthRegex = /^[0-9]{4}-(?:0[1-9]|1[0-2])$/;
      if (!monthRegex.test(month)) {
        throw new Error("Invalid month format. Please use YYYY-MM format.");
      }
    }

    const params: Record<string, any> = {};
    if (month) {
      params.month = month;
    }
    if (userIds && userIds.length > 0) {
      params.user_ids = userIds;
    }

    const response = await api.get("/token-usage/export-presigned-url", {
      params,
      paramsSerializer: (p: Record<string, any>) => {
        const search = new URLSearchParams();
        Object.entries(p).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (Array.isArray(value)) {
            value.forEach((v) => search.append(key, String(v)));
          } else {
            search.append(key, String(value));
          }
        });
        return search.toString();
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching token usage presigned URL:", error);
    throw error;
  }
};

// Add these chart data endpoint functions
export const getInputTokensChartData = async (
  user_id: string = '',
  test_mode: boolean = true
) => {
  try {
    const response = await api.get("/token-usage/chart-data/input-tokens", {
      params: {
        user_id,
        test_mode
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching input tokens chart data:", error);
    throw error;
  }
};

export const getOutputTokensChartData = async (
  user_id: string = '',
  test_mode: boolean = true
) => {
  try {
    const response = await api.get("/token-usage/chart-data/output-tokens", {
      params: {
        user_id,
        test_mode
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching output tokens chart data:", error);
    throw error;
  }
};

export const getSessionFilesChartData = async (
  user_id: string = '',
  test_mode: boolean = true
) => {
  try {
    const response = await api.get("/token-usage/chart-data/session-files", {
      params: {
        user_id,
        test_mode
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching session files chart data:", error);
    throw error;
  }
};

export const getBrainbankFilesChartData = async (
  user_id: string = '',
  test_mode: boolean = true
) => {
  try {
    const response = await api.get("/token-usage/chart-data/brainbank-files", {
      params: {
        user_id,
        test_mode
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching brainbank files chart data:", error);
    throw error;
  }
};

export const getSessionsChartData = async (
  user_id: string = '',
  test_mode: boolean = true
) => {
  try {
    const response = await api.get("/token-usage/chart-data/sessions", {
      params: {
        user_id,
        test_mode
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sessions chart data:", error);
    throw error;
  }
};

export const getAllMetricsChartData = async (
  user_id: string = '',
  test_mode: boolean = true
) => {
  try {
    const response = await api.get("/token-usage/chart-data/all-metrics", {
      params: {
        user_id,
        test_mode
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching all metrics chart data:", error);
    throw error;
  }
};

// Bedrock daily-cost API functions (admin-only; reads from DynamoDB, never Cost Explorer)
export const getBedrockDailyCost = async (page = 1, itemsPerPage = 7) => {
  try {
    const response = await api.get("/bedrock-cost/daily", {
      params: { page, items_per_page: itemsPerPage },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching daily Bedrock cost:", error);
    throw error;
  }
};

export const getBedrockMonthlyCost = async () => {
  try {
    const response = await api.get("/bedrock-cost/monthly");
    return response.data;
  } catch (error) {
    console.error("Error fetching monthly Bedrock cost:", error);
    throw error;
  }
};

export const getBedrockCostChartData = async () => {
  try {
    const response = await api.get("/bedrock-cost/chart-data/cost");
    return response.data;
  } catch (error) {
    console.error("Error fetching Bedrock cost chart data:", error);
    throw error;
  }
};

// VA Usage Tracking API functions
export const getVAUsageSummary = async (
  page = 1,
  itemsPerPage = 10,
  month?: string
) => {
  try {
    const params: any = {
      page,
      items_per_page: itemsPerPage,
    };
    if (month) {
      params.month = month;
    }
    const response = await api.get("/va-usage/summary", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching VA usage summary:", error);
    throw error;
  }
};

export const getVAUsageByHat = async (
  hatId: string,
  page = 1,
  itemsPerPage = 10,
  month?: string
) => {
  try {
    const params: any = {
      page,
      items_per_page: itemsPerPage,
    };
    if (month) {
      params.month = month;
    }
    const response = await api.get(`/va-usage/hat/${hatId}`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching VA usage by hat:", error);
    throw error;
  }
};

export const getVAUsageChartData = async (month?: string) => {
  try {
    const params: any = {};
    if (month) {
      params.month = month;
    }
    const response = await api.get("/va-usage/chart-data", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching VA usage chart data:", error);
    throw error;
  }
};

export const getVAUsageExportUrl = async (month?: string) => {
  try {
    const params: any = {};
    if (month) {
      params.month = month;
    }
    const response = await api.get("/va-usage/export", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching VA usage export URL:", error);
    throw error;
  }
};

// Project Management API functions
export const createProject = async (project_name: string): Promise<any> => {
  try {
    const response = await api.post("/project/", {
      project_name
    });
    console.log("Project created:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating project:", error);
    throw error;
  }
};

// export const getProjects = async (): Promise<any[]> => {
//   try {
//     const response = await api.get("/project/");
//     console.log("Projects retrieved:", response.data);
//     return response.data;
//   } catch (error) {
//     console.error("Error retrieving projects:", error);
//     throw error;
//   }
// };

export const getProjects = async (): Promise<any[]> => {
  try {
    const response = await api.get("/project/all");
    console.log("Projects retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving projects:", error);
    throw error;
  }
};

export const getProjectById = async (project_id: string): Promise<any> => {
  try {
    const response = await api.get(`/project/${project_id}`);
    console.log("Project retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving project:", error);
    throw error;
  }
};

export const updateProject = async (project_id: string, project_name: string): Promise<any> => {
  try {
    const response = await api.put(`/project/${project_id}`, {
      project_name
    });
    console.log("Project updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating project:", error);
    throw error;
  }
};

export interface DeleteProjectOptions {
  /** Super admin only: also delete every conversation in the project. */
  cascade?: boolean;
  /** Super admin only, needs cascade: also delete the BrainBanks those conversations used, with their files. */
  deleteBrainBanks?: boolean;
}

export const deleteProject = async (
  project_id: string,
  options: DeleteProjectOptions = {}
): Promise<any> => {
  try {
    const params: Record<string, boolean> = {};
    if (options.cascade) params.cascade = true;
    if (options.deleteBrainBanks) params.delete_brain_banks = true;
    const response = await api.delete(`/project/${project_id}`, { params });
    console.log("Project deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting project:", error);
    throw error;
  }
};

/**
 * Deletion window (super admin deletes). A cascade delete hides the project
 * and its conversations instead of removing them; until `purge_at` they can
 * be brought back from Chat History > Recently deleted.
 */
export interface RecentlyDeletedSession {
  id: string;
  title: string;
  user_id?: string;
  session_type?: string;
  deleted_at?: string;
  purge_at?: string;
  deleted_by?: string;
}

export interface RecentlyDeletedProject {
  id: string;
  project_name: string;
  user_id?: string;
  deleted_at?: string;
  purge_at?: string;
  deleted_by?: string;
  deletion_options?: { cascade?: boolean; delete_brain_banks?: boolean };
  conversation_count: number;
  sessions: RecentlyDeletedSession[];
}

export interface RecentlyDeleted {
  projects: RecentlyDeletedProject[];
  sessions: RecentlyDeletedSession[];
  retention_hours: number;
}

export const getRecentlyDeleted = async (): Promise<RecentlyDeleted> => {
  try {
    const response = await api.get("/recently-deleted/");
    return response.data;
  } catch (error) {
    console.error("Error retrieving recently deleted items:", error);
    throw error;
  }
};

export const restoreDeletedProject = async (project_id: string): Promise<any> => {
  try {
    const response = await api.post(`/recently-deleted/project/${project_id}/restore`);
    return response.data;
  } catch (error) {
    console.error("Error restoring project:", error);
    throw error;
  }
};

export const restoreDeletedSession = async (session_id: string): Promise<any> => {
  try {
    const response = await api.post(`/recently-deleted/session/${session_id}/restore`);
    return response.data;
  } catch (error) {
    console.error("Error restoring conversation:", error);
    throw error;
  }
};

export const addSessionToProject = async (project_id: string, session_id: string): Promise<any> => {
  try {
    const response = await api.post(`/project/${project_id}/session`, {
      session_id
    });
    console.log("Session added to project:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding session to project:", error);
    throw error;
  }
};

export const removeSessionFromProject = async (project_id: string, session_id: string): Promise<any> => {
  try {
    const response = await api.delete(`/project/${project_id}/session/${session_id}`);
    console.log("Session removed from project:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing session from project:", error);
    throw error;
  }
};

export const getAllUserProjects = async (): Promise<any[]> => {
  try {
    const response = await api.get("/project/all");
    console.log("All user projects retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving all user projects:", error);
    throw error;
  }
};

export const shareProject = async (
  project_id: string,
  user_ids: string[]
): Promise<any> => {
  try {
    const response = await api.post(`/project/${project_id}/share`, {
      user_ids,
    });
    console.log("Project shared:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error sharing project:", error);
    throw error;
  }
};

export const unshareProject = async (
  project_id: string,
  user_ids: string[]
): Promise<any> => {
  try {
    const response = await api.post(`/project/${project_id}/unshare`, {
      user_ids,
    });
    console.log("Project unshared:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error unsharing project:", error);
    throw error;
  }
};

export const getProjectSessionsWithOwners = async (
  project_id: string
): Promise<any[]> => {
  try {
    const response = await api.get(`/project/${project_id}/sessions`);
    console.log("Project sessions with owners retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving project sessions with owners:", error);
    throw error;
  }
};

// Knowledge Base Management
export const createKnowledgeBase = async (data: CreateKnowledgeBaseRequest) => {
  try {
    const response = await api.post("/knowledge-base/", data);
    console.log("Knowledge base created:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating knowledge base:", error);
    throw error;
  }
};

export const updateKnowledgeBase = async (
  index_id: string,
  data: UpdateKnowledgeBaseRequest
) => {
  try {
    const response = await api.put(`/knowledge-base/${index_id}`, data);
    console.log("Knowledge base updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating knowledge base:", error);
    throw error;
  }
};

export const deleteKnowledgeBase = async (index_id: string) => {
  try {
    const response = await api.delete(`/knowledge-base/${index_id}`);
    console.log("Knowledge base deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting knowledge base:", error);
    throw error;
  }
};

/* ── Resource locking (admin only) ──────────────────────────────────
 * An admin locks something they created; while locked it is exclusive to
 * them and every other user -- other admins included -- loses access.
 * Only the locking admin can unlock it. See app/utils/resource_lock.py.
 */

const lockPath = (
  resourceType: LockResourceType,
  resourceId: string
) => `/locks/${resourceType}/${encodeURIComponent(resourceId)}`;

// Files live in two tables, so the API needs to know which one. Mirrors the
// file_type parameter the admin file endpoints already take.
const lockParams = (fileType?: LockFileType) =>
  fileType ? { params: { file_type: fileType } } : undefined;

export const lockResource = async (
  resourceType: LockResourceType,
  resourceId: string,
  fileType?: LockFileType
): Promise<LockStateResponse> => {
  const response = await api.post(
    lockPath(resourceType, resourceId),
    null,
    lockParams(fileType)
  );
  return response.data;
};

export const unlockResource = async (
  resourceType: LockResourceType,
  resourceId: string,
  fileType?: LockFileType
): Promise<LockStateResponse> => {
  const response = await api.delete(
    lockPath(resourceType, resourceId),
    lockParams(fileType)
  );
  return response.data;
};

/**
 * True when a request failed because the resource is locked (HTTP 423).
 *
 * Distinguishing this from a 403 is why the API uses 423 at all: it means
 * "someone locked this", not "you lack permission", and the caller should
 * refresh its list rather than tell the user to ask for access.
 */
export const isLockedError = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 423;

/**
 * The message the API sent, or `fallback` when it didn't send one.
 *
 * Prefer this over a hardcoded string wherever the server knows something the
 * client doesn't -- "this VA is locked, unlock it first" tells the admin what
 * to do; "failed to add manager" leaves them guessing.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
};

export const getKnowledgeBase = async (index_id: string) => {
  try {
    const response = await api.get(`/knowledge-base/${index_id}`);
    console.log("Knowledge base retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving knowledge base:", error);
    throw error;
  }
};

export const listKnowledgeBases = async () => {
  try {
    const response = await api.get(`/knowledge-base/user/`);
    console.log("Knowledge bases retrieved:", response.data.knowledge_bases);
    return response.data.knowledge_bases;
  } catch (error) {
    console.error("Error retrieving knowledge bases:", error);
    throw error;
  }
};

export const listKnowledgeBasesPaginated = async (
  page: number,
  itemsPerPage: number,
  viewType: string = 'my_knowledge_base'
) => {
  try {
    const response = await api.get("/knowledge-base/paginated/", {
      params: {
        page,
        items_per_page: itemsPerPage,
        view_type: viewType,
      },
    });
    console.log("Paginated knowledge bases retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving paginated knowledge bases:", error);
    throw error;
  }
};

export const generateKnowledgeBasePresignedUploadUrl = async (
  file_name: string,
  index_name: string,
  knowledge_base_id: string,
  processing_mode: string,
  file_hash?: string,
  file_size?: number
) => {
  try {
    const response = await api.post(
      "/knowledge-base/generate-presigned-upload-url",
      {
        file_name,
        index_name,
        knowledge_base_id,
        processing_mode,
        ...(file_hash && { file_hash }),
        ...(file_size !== undefined && { file_size })
      }
    );
    console.log("Presigned URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating presigned URL:", error);
    throw error;
  }
};


// BrainBank Dropzone links (section 6.1 of docs/brainbank-dropzone-plan.md).
// The create response is the only call that ever returns the link URL.
export const createDropzoneLink = async (
  body: CreateDropzoneLinkRequest
): Promise<CreatedDropzoneLink> => {
  try {
    const response = await api.post("/dropzone-links", body);
    return response.data;
  } catch (error) {
    console.error("Error creating dropzone link:", error);
    throw error;
  }
};

export const listDropzoneLinks = async (
  knowledge_base_id: string
): Promise<DropzoneLink[]> => {
  try {
    const response = await api.get("/dropzone-links", {
      params: { knowledge_base_id },
    });
    return response.data.links ?? [];
  } catch (error) {
    console.error("Error listing dropzone links:", error);
    throw error;
  }
};

export const revokeDropzoneLink = async (link_id: string): Promise<DropzoneLink> => {
  try {
    const response = await api.post(`/dropzone-links/${link_id}/revoke`);
    return response.data;
  } catch (error) {
    console.error("Error revoking dropzone link:", error);
    throw error;
  }
};

/** The URL of an active link again; the API decrypts the stored token and logs the call. */
export const getDropzoneLinkUrl = async (link_id: string): Promise<string> => {
  try {
    const response = await api.get(`/dropzone-links/${link_id}/url`);
    return response.data.url;
  } catch (error) {
    console.error("Error fetching dropzone link URL:", error);
    throw error;
  }
};

export const listHeldDropzoneFiles = async (
  knowledge_base_id: string
): Promise<HeldDropzoneFile[]> => {
  try {
    const response = await api.get("/dropzone-links/held", {
      params: { knowledge_base_id },
    });
    return response.data.files ?? [];
  } catch (error) {
    console.error("Error listing held dropzone files:", error);
    throw error;
  }
};

export const approveDropzoneFile = async (file_id: string): Promise<void> => {
  try {
    await api.post(`/dropzone-links/files/${file_id}/approve`);
  } catch (error) {
    console.error("Error approving dropzone file:", error);
    throw error;
  }
};

export const rejectDropzoneFile = async (file_id: string): Promise<void> => {
  try {
    await api.post(`/dropzone-links/files/${file_id}/reject`);
  } catch (error) {
    console.error("Error rejecting dropzone file:", error);
    throw error;
  }
};

export const scanFileOnly = async (
  file: File,
  scan_enabled?: boolean,
  scan_id?: string
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (scan_enabled !== undefined) {
      formData.append("scan_enabled", scan_enabled ? "true" : "false");
    }
    if (scan_id) {
      formData.append("scan_id", scan_id);
    }

    const response = await api.post("/knowledge-base/scan-file-only", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error with file scan only:", error);
    throw error;
  }
};

export const uploadDirectKnowledgeBaseFile = async (
  file: File,
  knowledge_base_id: string,
  index_name: string,
  processing_mode: string = "auto",
  file_hash?: string,
  scan_enabled?: boolean,
  scan_id?: string
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("knowledge_base_id", knowledge_base_id);
    formData.append("index_name", index_name);
    formData.append("processing_mode", processing_mode);
    if (file_hash) {
      formData.append("file_hash", file_hash);
    }
    if (scan_enabled !== undefined) {
      formData.append("scan_enabled", scan_enabled ? "true" : "false");
    }
    if (scan_id) {
      formData.append("scan_id", scan_id);
    }

    const response = await api.post("/knowledge-base/upload-direct", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error with direct file upload:", error);
    throw error;
  }
};

export const checkFileDuplicate = async (
  file_name: string,
  file_size: number,
  file_hash?: string
): Promise<{ duplicate: boolean; existing_file_id?: string; existing_file_name?: string }> => {
  try {
    const response = await api.post("/admin/files/check-duplicate", {
      file_name,
      file_size,
      ...(file_hash && { file_hash })
    });
    return response.data;
  } catch (error) {
    console.error("Error checking file duplicate:", error);
    return { duplicate: false };
  }
};

export const generateKnowledgeBasePresignedDownloadUrl = async (
  file_name: string,
  index_name: string
) => {
  try {
    const response = await api.post(
      "/knowledge-base/generate-presigned-download-url",
      {
        file_name,
        index_name,
      }
    );
    console.log("Presigned URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating presigned URL:", error);
    throw error;
  }
};

export const getKnowledgeBasesByIds = async (kbIds: string[]) => {
  try {
    // Use the direct knowledge base endpoint without user filtering
    const response = await api.get(`/knowledge-base/`, {
      params: { ids: kbIds.join(',') }
    });
    console.log("Knowledge bases retrieved:", response.data.knowledge_bases);
    return response.data.knowledge_bases;
  } catch (error) {
    console.error("Error retrieving knowledge bases:", error);
    throw error;
  }
};

// New function to get source download URL based on kind
export const getSourceDownloadUrl = async (
  kind: string,
  file_name: string,
  session_id?: string,
  index_name?: string,
  expiry_seconds: number = 3600
) => {
  try {
    const payload: any = {
      kind,
      file_name,
      expiry_seconds
    };

    // Add conditional parameters based on kind
    if (kind === "brainbank" && index_name) {
      payload.index_name = index_name;
    } else if (kind === "conversation" && session_id) {
      payload.session_id = session_id;
    }

    const response = await api.post(
      "/session-file/source-download-url",
      payload
    );
    console.log("Source download URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating source download URL:", error);
    throw error;
  }
};

// Resolve stored→original filenames for a brainbank's source citations. Files are
// stored under a minted ASCII-safe name, so the model cites that; this lets the
// chat display the user's real filename (emoji/unicode preserved) on a [Source]
// citation without touching the download path (which resolves via the stored name).
export const getBrainbankSourceNames = async (
  index_name: string
): Promise<{ files: Array<{ file_name: string; original_file_name: string }> }> => {
  const response = await api.post("/session-file/brainbank-source-names", {
    index_name,
  });
  return response.data;
};

// File Management
export const getFilePath = async (file_id: string) => {
  try {
    const response = await api.get(`/files/${file_id}/path`);
    console.log("File path retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving file path:", error);
    throw error;
  }
};

export const generateFileDownloadUrlById = async (file_id: string) => {
  try {
    const response = await api.get(`/files/${file_id}/download-url`);
    console.log("File download URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating file download URL:", error);
    throw error;
  }
};

export const getFile = async (file_id: string): Promise<FileItem> => {
  try {
    const response = await api.get(`/files/${file_id}`);
    console.log("File retrieved:", response.data);
    return response.data as FileItem;
  } catch (error) {
    console.error("Error retrieving file:", error);
    throw error;
  }
};

export const generateSessionFilePresignedUploadUrl = async (
  file_name: string,
  file_size: string,
  session_id: string,
  original_file_name?: string
) => {
  try {
    const response = await api.post(
      "/session-file/generate-presigned-upload-url",
      {
        file_name,
        file_size,
        session_id,
        original_file_name,
      }
    );
    console.log("Session File Presigned URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating session file presigned URL:", error);
    throw error;
  }
};

export const generateSessionFilePresignedDownloadUrl = async (
  file_name: string,
  session_id: string
) => {
  try {
    const response = await api.post(
      "/session-file/generate-presigned-download-url",
      {
        file_name,
        session_id,
      }
    );
    console.log("Session File Presigned URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating session file presigned URL:", error);
    throw error;
  }
};

export const deleteFile = async (file_id: string) => {
  try {
    const response = await api.delete(
      `/knowledge-base/file/${file_id}`
    );
    console.log("File deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting file:", error);
    throw error;
  }
};

export const deleteSessionFile = async (file_id: string) => {
  try {
    const response = await api.delete(`/session-file/${file_id}`);
    console.log("Session File deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting session file:", error);
    throw error;
  }
};

export const getSessionFile = async (file_id: string) => {
  try {
    const response = await api.get(`/session-file/${file_id}`);
    console.log("Session File retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving session file:", error);
    throw error;
  }
};

export const getPrompts = async () => {
  try {
    const response = await api.get(`/prompt/generate/`);
    console.log("prompts retrieved:", response.data);
    return response.data.prompts;
  } catch (error) {
    console.error("Error retrieving knowledge bases:", error);
    throw error;
  }
};

export const updateSessionTitleByUser = async (
  session_id: string,
  new_title: string
) => {
  try {
    const response = await api.put(`/session/${session_id}/title/update`, {
      new_title: new_title,
    });
    console.log("Session title updated successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating session title:", error);
    throw error;
  }
};

// Model Service - keep this separate
export const modelService = {
  getAllModels: async () => {
    try {
      const response = await api.get('/models/all');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Handle unauthorized error
        throw new Error('Unauthorized');
      }
      console.error('Error fetching models:', error);
      throw error;
    }
  },

  updateEnabledModels: async (modelIds: string[]) => {
    try {
      const response = await api.put('/models/admin/enabled',
        { model_ids: modelIds }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating enabled models:', error);
      throw error;
    }
  },
  setDefaultModel: async (modelId: string) => {
    try {
      const response = await api.put('/models/admin/default',
        { model_id: modelId }
      );
      return response.data;
    } catch (error) {
      console.error('Error setting default model:', error);
      throw error;
    }
  },
  setDefaultVoiceModel: async (modelId: string) => {
    try {
      const response = await api.put('/models/admin/default-voice',
        { model_id: modelId }
      );
      return response.data;
    } catch (error) {
      console.error('Error setting default voice model:', error);
      throw error;
    }
  },

  // Cost tier drives automatic model selection for VAs (1 = economy .. 3 = premium).
  setModelCostTier: async (modelId: string, costTier: number) => {
    try {
      const response = await api.put('/models/admin/cost-tier',
        { model_id: modelId, cost_tier: costTier }
      );
      return response.data;
    } catch (error) {
      console.error('Error setting model cost tier:', error);
      throw error;
    }
  }
};

/**
 * Ask the backend to pick the most cost-effective model for a VA.
 *
 * BrainBanks and connectors are optional — when none are attached the choice is
 * made from the summary and instructions alone. Advisory only: the caller
 * decides whether to apply the result, and nothing is persisted until the VA
 * itself is saved.
 */
export const suggestVAModel = async (payload: {
  hat_name?: string;
  summary: string;
  directions: string;
  knowledge_base_ids?: string[];
  connector_ids?: string[];
  internet_browsing?: boolean;
}): Promise<VAModelSuggestion> => {
  const response = await api.post('/hat/suggest-model', {
    hat_name: payload.hat_name || null,
    summary: payload.summary,
    directions: payload.directions,
    knowledge_base_ids: payload.knowledge_base_ids || [],
    connector_ids: payload.connector_ids || [],
    internet_browsing: payload.internet_browsing || false,
  });
  return response.data;
};

// kyva connectors
export interface ConnectorSaveRequest {
  name: string;
  type: string;
}

export interface ConnectorStatusRequest {
  status: string;
}

export interface ConnectorInfoResponse {
  connectors: any[];
}

export interface Connector {
  id: string;
  name: string;
  type: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}
// Save connector (create or update)
export const saveConnector = async (data: ConnectorSaveRequest): Promise<any> => {
  try {
    const response = await api.post("/connectors/save", data);
    console.log("Connector saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error saving connector:", error);
    throw error;
  }
};

// Get all connectors info
export const getConnectorInfo = async (): Promise<ConnectorInfoResponse> => {
  try {
    const response = await api.get("/connectors/info");
    console.log("Connector info retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving connector info:", error);
    throw error;
  }
};

// Get connector by type
export const getConnectorByType = async (connectorType: string): Promise<any> => {
  try {
    const response = await api.get(`/connectors/type/${connectorType}`);
    console.log("Connector by type retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving connector by type:", error);
    throw error;
  }
};

// Update connector status
export const updateConnectorStatus = async (
  connectorId: string,
  status: string
): Promise<any> => {
  try {
    if (!["active", "inactive"].includes(status)) {
      throw new Error("Status must be either 'active' or 'inactive'");
    }

    const response = await api.patch(`/connectors/${connectorId}/status`, {
      status
    });
    console.log("Connector status updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating connector status:", error);
    throw error;
  }
};

export const getConnectorTypes = async (): Promise<{ connectors: { name: string; value: string }[] }> => {
  try {
    const response = await api.get("/connectors/types");
    console.log("Connector types retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving connector types:", error);
    throw error;
  }
};

// Create a new hat
export const createHat = async (
  hat_name: string,
  summary: string,
  department: string,
  initial_prompt: string,
  directions: string,
  is_public: boolean,
  feature_keys: object,
  model_id?: string
) => {
  try {
    const response = await api.post("/hat/", {
      hat_name,
      summary,
      department,
      initial_prompt,
      directions,
      is_public,
      feature_keys,
      model_id,
    });
    console.log("Hat created:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating hat:", error);
  }
};

export const duplicateHatToUser = async (
  hat_id: string,
  target_user_id: string,
  new_hat_name: string
) => {
  try {
    const response = await api.post(`/hat/${hat_id}/duplicate`, {
      target_user_id,
      new_hat_name
    });
    console.log("Hat duplicated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error duplicating hat:", error);
    throw error
  }
};

// Modify a hat using LLM instructions
export const modifyHatWithLLM = async (
  hat_id: string,
  instructions: string
) => {
  try {
    const response = await api.post(`/hat/${hat_id}/modify-with-llm`, {
      instructions,
    });
    return response.data;
  } catch (error) {
    console.error("Error modifying hat with LLM:", error);
    throw error;
  }
};

// Generate or modify VA fields using LLM (for Create/Edit forms, does not save).
//
// Streamed via fetch (NDJSON) rather than a blocking axios POST: large edits
// (e.g. a full instruction rewrite) can take well over 60s to generate, and a
// blocking request gets severed by the ALB's idle timeout - which surfaces in
// the browser as a misleading CORS/Network error on the resulting 504.
// Reading the response as a stream keeps bytes flowing so the connection never
// idles. onProgress (optional) receives the accumulated text as it streams.
export const generateVAWithLLM = async (
  instructions: string,
  currentVA?: { hat_name?: string; summary?: string; initial_prompt?: string; directions?: string },
  onProgress?: (accumulatedText: string) => void
): Promise<{ fields?: any; message?: string }> => {
  // Mirror the axios request interceptor's token-freshness handling, since this
  // raw fetch bypasses the interceptor.
  if (isTokenExpired() && reAuthenticateFunc) {
    if (isReAuthenticating) {
      await new Promise<void>((resolve) => requestQueue.push(() => resolve()));
    } else {
      await handleReAuthentication();
    }
  }

  const token = sessionStorage.getItem("authToken");

  const response = await fetch(`${endpoint}/hat/generate-with-llm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/x-ndjson",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      instructions,
      current_va: currentVA || null,
    }),
  });

  if (!response.ok || !response.body) {
    // Surface a server-provided detail when available; otherwise the status.
    let detail = `Request failed with status ${response.status}`;
    try {
      const data = await response.json();
      if (data?.detail) detail = data.detail;
    } catch {
      /* error body was not JSON */
    }
    const error: any = new Error(detail);
    error.response = { status: response.status, data: { detail } };
    console.error("Error generating VA with LLM:", error);
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulatedText = "";
  let result: { fields?: any; message?: string } | null = null;

  // Each NDJSON line is one event: delta | result | error.
  const processLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let event: any;
    try {
      event = JSON.parse(trimmed);
    } catch {
      // Ignore malformed/partial lines (shouldn't happen with NDJSON framing).
      return;
    }
    if (event.type === "delta") {
      accumulatedText += event.text || "";
      onProgress?.(accumulatedText);
    } else if (event.type === "result") {
      result = { fields: event.fields, message: event.message };
    } else if (event.type === "error") {
      const error: any = new Error(event.detail || "Failed to generate with AI");
      error.response = { data: { detail: event.detail } };
      throw error;
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);
        processLine(line);
      }
    }
    // Flush any trailing line that arrived without a final newline.
    if (buffer.trim()) processLine(buffer);
  } catch (error) {
    console.error("Error generating VA with LLM:", error);
    throw error;
  }

  if (!result) {
    const error: any = new Error("AI did not return a result. Please try again.");
    error.response = { data: { detail: error.message } };
    console.error("Error generating VA with LLM:", error);
    throw error;
  }

  return result;
};

// Update an existing hat by ID
export const updateHatById = async (
  hat_id: string,
  updatedData: Partial<{
    hat_name: string;
    summary: string;
    department: string;
    directions: string;
    is_public: boolean;
    feature_keys: object;
    model_id: string;
  }>
) => {
  try {
    const response = await api.put(`/hat/${hat_id}`, updatedData);
    console.log("Hat updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating hat:", error);
  }
};

// Delete a hat by ID
export const deleteHatById = async (hat_id: string) => {
  if (!hat_id) {
    console.error("Hat ID is required");
    return;
  }

  try {
    const response = await api.delete(`/hat/${hat_id}`);
    console.log("Hat deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting hat:", error);
  }
};

// Get a hat by ID
export const getHatById = async (hat_id: string) => {
  try {
    const response = await api.get(`/hat/${hat_id}`);
    console.log("Hat details:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching hat:", error);
  }
};

// Load paginated hats by user ID
export const loadHatsByUser = async (
  page: number = 1,
  items_per_page: number = 10
) => {
  try {
    const response = await api.get(`/hat/user/`, {
      params: {
        page,
        items_per_page,
      },
    });
    console.log("Paginated hats loaded:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error loading hats by user:", error);
    throw error; // Propagate the error to handle it in the caller function
  }
};

export const listHats = async () => {
  try {
    const response = await api.get(`/hat/user/`);
    console.log("hat list retrieved");
    return response.data.hats;
  } catch (error) {
    console.error("Error retrieving hat list: ", error);
    throw error;
  }
};

export const listHatsPaginated = async (
  page: number,
  itemsPerPage: number,
  filterType: string = 'my_hats' // Updated parameter name to match backend
) => {
  try {
    const response = await api.get("/hat/paginated/", {
      params: {
        page,
        items_per_page: itemsPerPage,
        filter_type: filterType, // Updated parameter name
      },
    });
    console.log("Paginated hats retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving paginated hat:", error);
    throw error;
  }
};

// If you want to add a specific favorites endpoint, add this function too:
export const listFavoriteHatsPaginated = async (
  page: number = 1,
  itemsPerPage: number = 10
) => {
  try {
    const response = await api.get("/hat/paginated/", {
      params: {
        page,
        items_per_page: itemsPerPage,
        filter_type: 'favorites',
      },
    });
    console.log("Paginated favorite hats retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving paginated favorite hats:", error);
    throw error;
  }
};

// Add hat to favorites
export const addHatToFavorites = async (
  hat_id: string,
  is_favorite: boolean
) => {
  try {
    const response = await api.post(`/hat/${hat_id}/favorite`, {
      is_favorite
    });
    console.log("Favorite status updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating favorite status:", error);
    throw error;
  }
};

// Get user's favorite hats
export const getUserFavorites = async (
  page: number = 1,
  items_per_page: number = 10
) => {
  try {
    const response = await api.get(`/hat/favorites`, {
      params: {
        page,
        items_per_page,
      },
    });
    console.log("User favorites loaded:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error loading user favorites:", error);
    throw error;
  }
};

// Remove hat from favorites
export const removeHatFromFavorites = async (hat_id: string) => {
  try {
    const response = await api.delete(`/hat/${hat_id}/favorite`);
    console.log("Hat removed from favorites:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing hat from favorites:", error);
    throw error;
  }
};

// Share hat with Users
export const shareHatWithUser = async (
  user_id: string,
  hat_ids: string[]
) => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.put(`/user/${user_id}/hat/share`, {
      hat_ids: hat_ids,
    });
    console.log("Hats shared with user:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error sharing hats with user:", error);
    throw error;
  }
};

// Unshare hat from users
export const unshareHatFromUser = async (
  user_id: string,
  hat_ids: string[]
) => {
  if (!user_id) {
    console.error("User ID is required");
    return;
  }

  try {
    const response = await api.put(`/user/${user_id}/hat/unshare`, {
      hat_ids: hat_ids,
    });
    console.log("Hats unshared from user:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error unsharing hats from user:", error);
    throw error;
  }
};

// Add a manager to a VA 
export const addManagerToHat = async (
  hat_id: string,
  manager_user_id: string
) => {
  try {
    const response = await api.post(`/hat/${hat_id}/managers/add`, {
      user_id: manager_user_id
    });
    console.log("Manager added to VA:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding manager to VA:", error);
    throw error;
  }
};

// Remove a manager from a VA 
export const removeManagerFromHat = async (
  hat_id: string,
  manager_user_id: string
) => {
  try {
    const response = await api.delete(`/hat/${hat_id}/managers/${manager_user_id}`);
    console.log("Manager removed from VA:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing manager from VA:", error);
    throw error;
  }
};

// Get all managers of a VA 
export const getHatManagers = async (hat_id: string) => {
  try {
    const response = await api.get(`/hat/${hat_id}/managers`);
    console.log("VA managers retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving VA managers:", error);
    throw error;
  }
};

// Get all users with access to a VA (owner, managers, shared users)
export const getVAAccessList = async (hat_id: string) => {
  try {
    const response = await api.get(`/hat/${hat_id}/access-list`);
    return response.data;
  } catch (error) {
    console.error("Error retrieving VA access list:", error);
    throw error;
  }
};

// Update complete list of managers for a VA
export const updateHatManagers = async (
  hat_id: string,
  manager_user_ids: string[]
) => {
  try {
    const response = await api.put(`/hat/${hat_id}/managers`, {
      manager_user_ids
    });
    console.log("VA managers updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating VA managers:", error);
    throw error;
  }
};

// Get VAs where current user is a manager 
export const getMyManagedHats = async (
  page: number = 1,
  items_per_page: number = 10
) => {
  try {
    const response = await api.get(`/hat/managers/my-managed`, {
      params: {
        page,
        items_per_page
      }
    });
    console.log("My managed VAs retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving my managed VAs:", error);
    throw error;
  }
};

// Check user access to a VA
export const checkHatAccess = async (hat_id: string) => {
  try {
    const response = await api.get(`/hat/${hat_id}/access-check`);
    console.log("VA access check:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error checking VA access:", error);
    throw error;
  }
};

// Save license with logical_license_id
export const saveLicense = async (logical_license_id: string) => {
  if (!logical_license_id) {
    console.error("Logical license ID is required");
    return;
  }

  try {
    const response = await api.post("/license/save", {
      logical_license_id,
    });
    console.log("License saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error saving license:", error);
    throw error;
  }
};

// Get license information
export const getLicenseInfo = async (): Promise<LicenseInfo> => {
  try {
    const response = await api.get("/license/info");
    console.log("License info:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching license info:", error);
    throw error;
  }
};


// Add this interface to your types.ts or in apiService.ts if you prefer
interface TavilyApiResponse {
  success: boolean;
  message: string;
  api_key?: string;
}

// Get Tavily API Key
export const getTavilyApiKey = async (): Promise<TavilyApiResponse> => {
  try {
    const response = await api.get('/internet-license/tavily-api-key');
    console.log('Tavily API key retrieved:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        console.error('Tavily API key not found:', error.response.data.detail);
        return {
          success: false,
          message: error.response.data.detail
        };
      }
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data.detail);
      }
    }
    console.error('Error retrieving Tavily API key:', error);
    throw error;
  }
};

// Save Tavily API Key
export const saveTavilyApiKey = async (api_key: string): Promise<TavilyApiResponse> => {
  try {
    if (!api_key?.trim()) {
      throw new Error('API key is required');
    }

    const response = await api.post('/internet-license/tavily-api-save', {
      api_key
    });
    console.log('Tavily API key saved:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400) {
        console.error('Bad request:', error.response.data.detail);
      }
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data.detail);
      }
    }
    console.error('Error saving Tavily API key:', error);
    throw error;
  }
};

// Add this interface to your types.ts or in apiService.ts if you prefer
interface CanliApiResponse {
  success: boolean;
  message: string;
  api_key?: string;
}

// Get Tavily API Key
export const getCanliiApiKey = async (): Promise<CanliApiResponse> => {
  try {
    const response = await api.get('/canlii-license/canlii-api-key');
    console.log('Tavily API key retrieved:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        console.error('Canlii API key not found:', error.response.data.detail);
        return {
          success: false,
          message: error.response.data.detail
        };
      }
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data.detail);
      }
    }
    console.error('Error retrieving Canlii API key:', error);
    throw error;
  }
};

// Save Tavily API Key
export const saveCanliiApiKey = async (api_key: string): Promise<CanliApiResponse> => {
  try {
    if (!api_key?.trim()) {
      throw new Error('API key is required');
    }

    const response = await api.post('/canlii-license/canlii-api-save', {
      api_key
    });
    console.log('Canlii API key saved:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400) {
        console.error('Bad request:', error.response.data.detail);
      }
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data.detail);
      }
    }
    console.error('Error saving Canlii API key:', error);
    throw error;
  }
};

// Add this interface to your types.ts or in apiService.ts if you prefer
interface AlphaVantageApiResponse {
  success: boolean;
  message: string;
  api_key?: string;
}

// Get Alpha Vantage API Key
export const getAlphaVantageApiKey = async (): Promise<AlphaVantageApiResponse> => {
  try {
    const response = await api.get('/alpha-vantage-license/alpha-vantage-api-key');
    console.log('Alpha Vantage API key retrieved:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        console.error('Alpha Vantage API key not found:', error.response.data.detail);
        return {
          success: false,
          message: error.response.data.detail
        };
      }
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data.detail);
      }
    }
    console.error('Error retrieving Alpha Vantage API key:', error);
    throw error;
  }
};

// Save Alpha Vantage API Key
export const saveAlphaVantageApiKey = async (api_key: string): Promise<AlphaVantageApiResponse> => {
  try {
    if (!api_key?.trim()) {
      throw new Error('API key is required');
    }

    const response = await api.post('/alpha-vantage-license/alpha-vantage-api-save', {
      api_key
    });
    console.log('Alpha Vantage API key saved:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400) {
        console.error('Bad request:', error.response.data.detail);
      }
      if (error.response?.status === 500) {
        console.error('Server error:', error.response.data.detail);
      }
    }
    console.error('Error saving Alpha Vantage API key:', error);
    throw error;
  }
};

// Add these interfaces at the top with your other interfaces
interface ClientLogoUploadRequest {
  file_name: string;
  file_size: string;
  client_name?: string;
}

interface PresignedUrlResponse {
  file_id: string;
  presigned_url: string;
  s3_key?: string;
  bucket_name?: string;
  expires_in?: number;
}

interface ClientLogoResponse {
  id: string;
  file_name: string;
  file_type: string;
  file_size: string;
  status: string;
  is_current: string;
  uploaded_by: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  s3_key: string;
  s3_bucket: string;
  version: number;
  original_file_name?: string;
  uploaded_at?: string;
  client_name?: string;
}

// Generate presigned URL for client logo upload
export const generateClientLogoPresignedUploadUrl = async (
  file_name: string,
  file_size: string,
  client_name: string
): Promise<PresignedUrlResponse> => {
  try {

    if (!client_name || !client_name.trim()) {
      throw new Error('Client name is required');
    }

    const payload: ClientLogoUploadRequest = {
      file_name,
      file_size,
      client_name: client_name.trim(),
    };

    const response = await api.post("/client-logo/generate-presigned-upload-url", payload);
    console.log("Client logo presigned URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating client logo presigned URL:", error);
    throw error;
  }
};

// Upadte client name
export const updateClientLogoClientName = async (
  logo_id: string,
  client_name: string
): Promise<{ message: string }> => {
  try {
    const response = await api.put(`/client-logo/${logo_id}/client-name`, {
      client_name: client_name.trim()
    });
    console.log("Client logo client name updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating client logo client name:", error);
    throw error;
  }
};

// Get current active client logo
export const getCurrentClientLogo = async (): Promise<ClientLogoResponse | null> => {
  try {
    const response = await api.get("/client-logo/current");
    console.log("Current client logo retrieved:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log("No current client logo found");
      return null;
    }
    console.error("Error retrieving current client logo:", error);
    throw error;
  }
};

// Get all client logos for the user
export const getAllClientLogos = async (): Promise<{ data: ClientLogoResponse[] }> => {
  try {
    const response = await api.get("/client-logo/all");
    console.log("All client logos retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving all client logos:", error);
    throw error;
  }
};

// Get specific client logo by ID
export const getClientLogoById = async (logo_id: string): Promise<ClientLogoResponse> => {
  try {
    const response = await api.get(`/client-logo/${logo_id}`);
    console.log("Client logo retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving client logo:", error);
    throw error;
  }
};

// Update client logo status (after S3 upload)
export const updateClientLogoStatus = async (
  logo_id: string,
  status: string
): Promise<{ message: string }> => {
  try {
    const response = await api.put(`/client-logo/${logo_id}/status`, {
      status
    });
    console.log("Client logo status updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating client logo status:", error);
    throw error;
  }
};

// Activate a specific client logo (set as current)
export const activateClientLogo = async (
  logo_id: string
): Promise<{ message: string }> => {
  try {
    const response = await api.put(`/client-logo/${logo_id}/activate`);
    console.log("Client logo activated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error activating client logo:", error);
    throw error;
  }
};

// Delete client logo
export const deleteClientLogo = async (logo_id: string): Promise<{ message: string }> => {
  try {
    const response = await api.delete(`/client-logo/${logo_id}`);
    console.log("Client logo deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting client logo:", error);
    throw error;
  }
};

// Generate download URL for client logo
export const generateClientLogoDownloadUrl = async (
  logo_id: string
): Promise<PresignedUrlResponse> => {
  try {
    const response = await api.post(`/client-logo/${logo_id}/download-url`, {});
    console.log("Client logo download URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating client logo download URL:", error);
    throw error;
  }
};

// Upload file directly to S3 using presigned URL with progress tracking
export const uploadFileToS3 = async (
  presigned_url: string,
  file: File,
  onProgress?: (progress: number) => void,
  metadata?: Record<string, string>
): Promise<void> => {
  try {
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log("File uploaded to S3 successfully");
          resolve();
        } else {
          console.error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`);
          console.error('Response:', xhr.responseText);
          reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', (event) => {
        console.error('S3 upload error event:', event);
        reject(new Error('S3 upload failed'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('S3 upload timed out'));
      });

      xhr.open('PUT', presigned_url);

      // Parse URL to extract required headers from query parameters
      const url = new URL(presigned_url);
      const signedHeaders = url.searchParams.get('X-Amz-SignedHeaders');

      // Set required headers based on what's in the signed headers
      if (signedHeaders) {
        const headersList = signedHeaders.split(';');

        // Always set Content-Type if it's in signed headers
        if (headersList.includes('content-type')) {
          xhr.setRequestHeader('Content-Type', file.type);
        }

        // Set cache-control if it's in signed headers
        if (headersList.includes('cache-control')) {
          xhr.setRequestHeader('Cache-Control', 'no-cache');
        }

        // Set metadata headers if they exist in signed headers
        if (metadata) {
          Object.entries(metadata).forEach(([key, value]) => {
            const metaKey = `x-amz-meta-${key.toLowerCase()}`;
            if (headersList.includes(metaKey)) {
              xhr.setRequestHeader(metaKey, value);
            }
          });
        }

        // Set server-side encryption if it's in signed headers
        if (headersList.includes('x-amz-server-side-encryption')) {
          xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
        }
      } else {
        // Fallback: set basic headers
        xhr.setRequestHeader('Content-Type', file.type);
      }

      // Set timeout to 5 minutes
      xhr.timeout = 5 * 60 * 1000;

      console.log('Uploading file to S3:', {
        url: presigned_url,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        signedHeaders: signedHeaders
      });

      xhr.send(file);
    });
  } catch (error) {
    console.error("Error uploading file to S3:", error);
    throw error;
  }
};

// Get the most recent active client logo from all accounts
export const getLatestClientLogoGlobal = async (): Promise<ClientLogoResponse | null> => {
  try {
    const response = await api.get("/client-logo/latest-global");
    console.log("Latest client logo from all accounts retrieved:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log("No active client logos found globally");
      return null;
    }
    console.error("Error retrieving latest client logo globally:", error);
    throw error;
  }
};

// Generate download URL for client logo (global access)
export const generateClientLogoDownloadUrlGlobal = async (
  logo_id: string
): Promise<PresignedUrlResponse> => {
  try {
    const response = await api.post(`/client-logo/${logo_id}/download-url-global`, {});
    console.log("Client logo global download URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating client logo global download URL:", error);
    throw error;
  }
};

//get all logos from all accounts
export const getAllClientLogosFromAllAccounts = async (): Promise<{ data: ClientLogoResponse[] }> => {
  try {
    const response = await api.get("/client-logo/all-accounts");
    console.log("All client logos from all accounts retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving all client logos from all accounts:", error);
    throw error;
  }
};

// Utility function to validate file type for client logos
export const validateClientLogoFile = (file: File): { isValid: boolean; error?: string } => {
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!validTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Invalid file type. Please use PNG, JPG, JPEG, or SVG.'
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File too large. Maximum size is 10MB.'
    };
  }

  return { isValid: true };
};

// Department Management API functions
export interface Department {
  id: string;
  department_name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDepartmentRequest {
  department_name: string;
}

export interface UpdateDepartmentRequest {
  department_name?: string;
}

export interface PaginatedDepartmentsResponse {
  departments: Department[];
  pagination: {
    page: number;
    items_per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    next_page: number | null;
  };
}

// Create a new department (admin only) 
export const createDepartment = async (
  department_name: string
): Promise<{ department_id: string }> => {
  try {
    const response = await api.post("/department/", {
      department_name
    });
    console.log("Department created:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating department:", error);
    throw error;
  }
};

// Get department by ID
export const getDepartmentById = async (
  department_id: string
): Promise<Department> => {
  try {
    const response = await api.get(`/department/${department_id}`);
    console.log("Department retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving department:", error);
    throw error;
  }
};

// Update department 
export const updateDepartment = async (
  department_id: string,
  data: UpdateDepartmentRequest
): Promise<{ department_id: string }> => {
  try {
    const response = await api.put(`/department/${department_id}`, data);
    console.log("Department updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating department:", error);
    throw error;
  }
};

// Delete department 
export const deleteDepartment = async (
  department_id: string
): Promise<{ department_id: string; message: string }> => {
  try {
    const response = await api.delete(`/department/${department_id}`);
    console.log("Department deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting department:", error);
    throw error;
  }
};

// List all departments
export const listDepartments = async (): Promise<{ departments: Department[]; user_department_count: number }> => {
  try {
    const response = await api.get("/department/user/");
    console.log("Departments retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving departments:", error);
    throw error;
  }
};

// List departments with pagination and filters 
export const listDepartmentsPaginated = async (
  page: number = 1,
  items_per_page: number = 10,
  filter_type: string = 'my_departments'
): Promise<PaginatedDepartmentsResponse> => {
  try {
    const response = await api.get("/department/paginated/", {
      params: {
        page,
        items_per_page,
        filter_type
      }
    });
    console.log("Paginated departments retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving paginated departments:", error);
    throw error;
  }
};

// Get user department statistics - NO CHANGE NEEDED
export const getUserDepartmentStats = async (user_id: string): Promise<{
  user_id: string;
  user_role: string;
  department_count: number;
  department_ids: string[];
  message: string;
}> => {
  try {
    const response = await api.get(`/department/user-stats/${user_id}`);
    console.log("User department stats retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving user department stats:", error);
    throw error;
  }
};

export const assignHatToDepartment = async (
  hat_id: string,
  department_name: string,
  user_id: string
): Promise<any> => {
  try {
    const response = await api.put(`/hat/${hat_id}/assign-department`, {}, {
      params: {
        user_id,
        department_name
      }
    });
    console.log("Hat department assigned:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error assigning hat to department:", error);
    throw error;
  }
};

// Remove hat from department
export const removeHatFromDepartment = async (
  hat_id: string
): Promise<{ message: string; data: { hat_id: string } }> => {
  try {
    const response = await api.delete(`/hat/${hat_id}/remove-department`);
    console.log("Hat removed from department:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing hat from department:", error);
    throw error;
  }
};

// Get hats by department name 
export const getHatsByDepartmentName = async (
  department_name: string,
  page: number = 1,
  items_per_page: number = 10
): Promise<{
  message: string;
  department_name: string;
  data: {
    hats: any[];
    pagination: {
      page: number;
      items_per_page: number;
      total_items: number;
      total_pages: number;
      has_next: boolean;
      next_page: number | null;
    };
  };
}> => {
  try {
    const response = await api.get(`/hat/by-department/${department_name}`, {
      params: {
        page,
        items_per_page
      }
    });
    console.log("Hats by department retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving hats by department:", error);
    throw error;
  }
};

export interface GoogleDriveAuthResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const authenticateGoogleDrive = async (
  code: string
): Promise<GoogleDriveAuthResponse> => {
  try {
    const response = await api.post('/gdrive/auth', {
      code: code,
    });

    return {
      success: true,
      message: response.data.message || 'Google Drive connected successfully',
    };
  } catch (error) {
    console.error('Error authenticating Google Drive:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};


export const checkGoogleDriveConnection = async (): Promise<boolean> => {
  try {
    const response = await api.get('/gdrive/auth/status');
    const data = response.data;
    return data.connected || false;
  } catch (error) {
    console.error('Error checking Google Drive connection:', error);
    return false;
  }
};

export interface GoogleDriveDisconnectResponse {
  success: boolean;
  message: string;
  disconnected_email?: string;
  error?: string;
}

// ✅ ADD THIS NEW FUNCTION
export const disconnectGoogleDrive = async (): Promise<GoogleDriveDisconnectResponse> => {
  try {
    const response = await api.delete('/gdrive/auth/disconnect');

    return {
      success: true,
      message: response.data.message || 'Google Drive account disconnected successfully',
      disconnected_email: response.data.disconnected_email,
    };
  } catch (error) {
    console.error('Error disconnecting Google Drive:', error);

    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: '',
        error: error.response?.data?.detail || error.message || 'Failed to disconnect Google Drive',
      };
    }

    return {
      success: false,
      message: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

// Google Drive File Picker
export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  size?: string;
  iconLink?: string;
  webViewLink?: string;
  parents?: string[];
}

export interface GoogleDriveFilesResponse {
  success: boolean;
  files: GoogleDriveFile[];
  next_page_token?: string;
}

export const listGoogleDriveFiles = async (
  pageToken?: string,
  query?: string,
  pageSize: number = 50,
  folderId?: string
): Promise<GoogleDriveFilesResponse> => {
  try {
    const params: Record<string, string | number> = { page_size: pageSize };
    if (pageToken) params.page_token = pageToken;
    if (query) params.query = query;
    if (folderId) params.folder_id = folderId;

    const response = await api.get('/gdrive/files', { params });
    return {
      success: true,
      files: response.data.files || [],
      next_page_token: response.data.next_page_token,
    };
  } catch (error) {
    console.error('Error listing Google Drive files:', error);
    return {
      success: false,
      files: [],
      next_page_token: undefined,
    };
  }
};

// Add these email visibility functions to apiService.ts

// Interface for email visibility response
export interface EmailVisibilityResponse {
  showEmailAddresses: boolean;
}

// Get the current global email visibility setting
export const getEmailVisibilitySetting = async (): Promise<EmailVisibilityResponse> => {
  try {
    const response = await api.get('/app-settings/email-visibility');
    console.log('Email visibility setting retrieved:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error retrieving email visibility setting:', error);
    throw error;
  }
};

// Update the global email visibility setting (admin only)
export const updateEmailVisibilitySetting = async (
  showEmailAddresses: boolean
): Promise<{ success: boolean; showEmailAddresses: boolean }> => {
  try {
    const response = await api.put(
      `/app-settings/email-visibility`,
      { showEmailAddresses }
    );
    console.log('Email visibility setting updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating email visibility setting:', error);
    throw error;
  }
};

export interface GoogleEmailAuthResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface GoogleEmailDisconnectResponse {
  success: boolean;
  message: string;
  disconnected_email?: string;
  error?: string;
}

export const authenticateGoogleEmail = async (
  code: string
): Promise<GoogleEmailAuthResponse> => {
  try {
    const response = await api.post('/gmail/auth', {
      code: code,
    });

    return {
      success: true,
      message: response.data.message || 'Google Email connected successfully',
    };
  } catch (error) {
    console.error('Error authenticating Google Email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};


export const checkGoogleEmailConnection = async (): Promise<boolean> => {
  try {
    const response = await api.get('/gmail/auth/status');
    const data = response.data;
    return data.connected || false;
  } catch (error) {
    console.error('Error checking Google Email connection:', error);
    return false;
  }
};

export const disconnectGoogleEmail = async (): Promise<GoogleEmailDisconnectResponse> => {
  try {
    const response = await api.delete('/gmail/auth/disconnect');

    return {
      success: true,
      message: response.data.message || 'Gmail account disconnected successfully',
      disconnected_email: response.data.disconnected_email,
    };
  } catch (error) {
    console.error('Error disconnecting Google Email:', error);

    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: '',
        error: error.response?.data?.detail || error.message || 'Failed to disconnect Gmail',
      };
    }

    return {
      success: false,
      message: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export interface GoogleCalendarAuthResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface GoogleCalendarDisconnectResponse {
  success: boolean;
  message: string;
  disconnected_email?: string;
  error?: string;
}

export const authenticateGoogleCalendar = async (
  code: string
): Promise<GoogleCalendarAuthResponse> => {
  try {
    const response = await api.post('/gcalendar/auth', {
      code: code,
    });

    return {
      success: true,
      message: response.data.message || 'Google Calendar connected successfully',
    };
  } catch (error) {
    console.error('Error authenticating Google Calendar:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const checkGoogleCalendarConnection = async (): Promise<boolean> => {
  try {
    const response = await api.get('/gcalendar/auth/status');
    const data = response.data;
    return data.connected || false;
  } catch (error) {
    console.error('Error checking Google Calendar connection:', error);
    return false;
  }
};

export const disconnectGoogleCalendar = async (): Promise<GoogleCalendarDisconnectResponse> => {
  try {
    const response = await api.delete('/gcalendar/auth/disconnect');

    return {
      success: true,
      message: response.data.message || 'Google Calendar account disconnected successfully',
      disconnected_email: response.data.disconnected_email,
    };
  } catch (error) {
    console.error('Error disconnecting Google Calendar:', error);

    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: '',
        error: error.response?.data?.detail || error.message || 'Failed to disconnect Google Calendar',
      };
    }

    return {
      success: false,
      message: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export interface OutlookAuthResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface OutlookDisconnectResponse {
  success: boolean;
  message: string;
  disconnected_email?: string;
  error?: string;
}

export const authenticateOutlook = async (
  code: string
): Promise<OutlookAuthResponse> => {
  try {
    const response = await api.post('/outlook/auth', {
      code: code,
    });

    return {
      success: true,
      message: response.data.message || 'Outlook connected successfully',
    };
  } catch (error) {
    console.error('Error authenticating Outlook:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const checkOutlookConnection = async (): Promise<boolean> => {
  try {
    const response = await api.get('/outlook/auth/status');
    const data = response.data;
    return data.connected || false;
  } catch (error) {
    console.error('Error checking Outlook connection:', error);
    return false;
  }
};

export const disconnectOutlook = async (): Promise<OutlookDisconnectResponse> => {
  try {
    const response = await api.delete('/outlook/auth/disconnect');

    return {
      success: true,
      message: response.data.message || 'Outlook account disconnected successfully',
      disconnected_email: response.data.disconnected_email,
    };
  } catch (error) {
    console.error('Error disconnecting Outlook:', error);

    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: '',
        error: error.response?.data?.detail || error.message || 'Failed to disconnect Outlook',
      };
    }

    return {
      success: false,
      message: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

// Speech-to-text transcription using Amazon Transcribe
export const transcribeAudio = async (audioBlob: Blob): Promise<{ text: string }> => {
  try {
    const formData = new FormData();
    formData.append("audio", audioBlob, "recording.wav");

    const response = await api.post("/transcribe/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error transcribing audio:", error);
    throw error;
  }
};

export const getAllFilesForAdmin = async (
  page: number = 1,
  page_size: number = 20,
  search?: string,
  scan_status?: string,
  finding_type?: string,
  cancelToken?: import("axios").CancelToken
): Promise<AdminFileManagementResponse> => {
  try {
    const params: any = { page, page_size };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (scan_status) {
      params.scan_status = scan_status;
    }
    if (finding_type) {
      params.finding_type = finding_type;
    }
    const response = await api.get("/admin/files", { params, cancelToken });
    console.log("Admin file management data retrieved:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled:", (error as any).message);
      throw error;
    }
    console.error("Error retrieving admin file management data:", error);
    throw error;
  }
};

export const getUserAccessibleFiles = async (
  page: number = 1,
  page_size: number = 20,
  search?: string,
  scan_status?: string,
  finding_type?: string,
  cancelToken?: import("axios").CancelToken
): Promise<UserFileManagementResponse> => {
  try {
    const params: any = { page, page_size };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (scan_status) {
      params.scan_status = scan_status;
    }
    if (finding_type) {
      params.finding_type = finding_type;
    }
    const response = await api.get("/admin/user-files", { params, cancelToken });
    console.log("User accessible files retrieved:", response.data);
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("Request canceled:", (error as any).message);
      throw error;
    }
    console.error("Error retrieving user accessible files:", error);
    throw error;
  }
};

export const searchAllFilesForAdmin = async (
  search: string,
  page: number = 1,
  page_size: number = 20
): Promise<AdminFileManagementResponse> => {
  try {
    const params: any = { search: search.trim(), page, page_size };
    const response = await api.get("/admin/files/search", { params });
    console.log("Admin file search results retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error searching admin files:", error);
    throw error;
  }
};

export const searchUserAccessibleFiles = async (
  search: string,
  page: number = 1,
  page_size: number = 20
): Promise<UserFileManagementResponse> => {
  try {
    const params: any = { search: search.trim(), page, page_size };
    const response = await api.get("/admin/user-files/search", { params });
    console.log("User file search results retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error searching user files:", error);
    throw error;
  }
};


export const generateAdminFileDownloadUrl = async (
  file_id: string,
  file_type: 'session' | 'brainbank'
): Promise<{ file_id: string; presigned_url: string }> => {
  try {
    const params: any = { file_type };

    const response = await api.get(`/admin/files/${file_id}/download-url`, {
      params
    });
    console.log("Admin file download URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating admin file download URL:", error);
    throw error;
  }
};

export const generateUserFileDownloadUrl = async (
  file_id: string,
  file_type: 'session' | 'brainbank'
): Promise<{ file_id: string; presigned_url: string }> => {
  try {
    const params: any = { file_type };

    const response = await api.get(`/admin/user-files/${file_id}/download-url`, {
      params
    });
    console.log("User file download URL generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error generating user file download URL:", error);
    throw error;
  }
};

export const getAdminFilePath = async (
  file_id: string,
  file_type: 'session' | 'brainbank'
): Promise<any> => {
  try {
    const params: any = { file_type };

    const response = await api.get(`/admin/files/${file_id}/path`, {
      params
    });
    console.log("Admin file path retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving admin file path:", error);
    throw error;
  }
};

// File Rescan API Endpoints
export const rescanFile = async (file_id: string): Promise<any> => {
  try {
    const response = await api.post(`/knowledge-base/rescan-file/${file_id}`);
    return response.data;
  } catch (error) {
    console.error(`Error rescanning file ${file_id}:`, error);
    throw error;
  }
};

export const rescanFiles = async (file_ids: string[]): Promise<any> => {
  try {
    const response = await api.post(`/knowledge-base/rescan-files`, { file_ids });
    return response.data;
  } catch (error) {
    console.error("Error rescanning multiple files:", error);
    throw error;
  }
};

export const deleteAdminFile = async (
  file_id: string,
  file_type: 'session' | 'brainbank'
): Promise<any> => {
  try {
    const params: any = { file_type };

    const response = await api.delete(`/admin/files/${file_id}`, {
      params
    });
    console.log("File deleted by admin:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting file as admin:", error);
    throw error;
  }
};

export const deleteUserFile = async (
  file_id: string,
  file_type: 'session' | 'brainbank'
): Promise<any> => {
  try {
    const params: any = { file_type };

    const response = await api.delete(`/admin/user-files/${file_id}`, {
      params
    });
    console.log("User file deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting user file:", error);
    throw error;
  }
};

// Release Version Management
export interface ReleaseVersion {
  id: string;
  sequence_number: number;
  version: string;
  notes: string;
  released_at: string;
}

export const getLatestReleaseVersion = async (): Promise<ReleaseVersion> => {
  try {
    const response = await api.get("/release-version/latest");
    console.log("Latest release version retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving latest release version:", error);
    throw error;
  }
};

// Get all release versions
export const getAllReleaseVersions = async (): Promise<ReleaseVersion[]> => {
  try {
    const response = await api.get("/release-version/");
    console.log("All release versions retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving all release versions:", error);
    throw error;
  }
};

// Memory Settings API functions
export const toggleLongTermMemory = async (enabled: boolean): Promise<any> => {
  try {
    const response = await api.put("/user/me/long-term-memory", {
      enabled
    });
    console.log("Long-term memory setting updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating long-term memory setting:", error);
    throw error;
  }
};

export const toggleShortTermMemory = async (enabled: boolean): Promise<any> => {
  try {
    const response = await api.put("/user/me/short-term-memory", {
      enabled
    });
    console.log("Short-term memory setting updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating short-term memory setting:", error);
    throw error;
  }
};

export const updateUserTheme = async (
  theme: "light" | "dark" | "system"
): Promise<any> => {
  try {
    const response = await api.put("/user/me/theme", { theme });
    return response.data;
  } catch (error) {
    console.error("Error updating theme preference:", error);
    throw error;
  }
};

//Email service with attachment support
export const sendFeedback = async (
  feedbackData: FeedbackRequest,
  attachments?: File[]
): Promise<FeedbackResponse> => {
  try {
    // No attachments - send as JSON
    if (!attachments || attachments.length === 0) {
      const response = await api.post("/feedback/send", feedbackData);
      console.log("Feedback sent successfully (no attachments):", response.data);
      return response.data;
    }

    // With attachments - send as FormData 
    const formData = new FormData();

    // Add text fields
    formData.append('name', feedbackData.name);
    formData.append('message', feedbackData.message);
    formData.append('from_email', feedbackData.from_email);
    formData.append('to_email', feedbackData.to_email);
    formData.append('subject', feedbackData.subject);

    // Add file attachments
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    // Send as multipart/form-data (Content-Type set automatically by axios)
    const response = await api.post("/feedback/send", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log("Feedback sent successfully (with attachments):", response.data);
    return response.data;

  } catch (error) {
    console.error("Error sending feedback:", error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || "Failed to send feedback");
    }
    throw error;
  }
};

//guardrails functions
export const getGuardrailsSettings = async (): Promise<GuardrailSettingsResponse> => {
  try {
    const response = await api.get("/app-settings/guardrails");
    console.log("Guardrails settings retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving guardrails settings:", error);
    throw error;
  }
};

export const updateGuardrailsSettings = async (
  settings: GuardrailSettings
): Promise<GuardrailSettingsResponse> => {
  try {
    const response = await api.put("/app-settings/guardrails", settings);
    console.log("Guardrails settings updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating guardrails settings:", error);
    throw error;
  }
};

export const getGuardrailsStats = async (): Promise<any> => {
  try {
    const response = await api.get("/app-settings/guardrails/stats");
    console.log("Guardrails statistics retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving guardrails statistics:", error);
    throw error;
  }
};

export const updateCognitoEmailTemplate = async (): Promise<{
  success: boolean;
  url_updated?: string;
  error?: string;
}> => {
  try {
    const response = await api.post("/user/admin/update-cognito-template");
    console.log("Cognito email template updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating Cognito email template:", error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || "Failed to update Cognito template");
    }
    throw error;
  }
};


// TODO do not remove the code, it works well with authentication mechanism in local dev mode, useful for feature updates
// export const createOrUpdateUser = async () => {
//   try {
//     const response = await api.post('/user/');
//     console.log('User created or updated:', response.data);
//     return response.data;
//   } catch (error) {
//     console.error('Error creating or updating user:', error);
//   }
// };

// ============================================================================
// Workflow API calls
// ============================================================================

export const createWorkflow = async (
  workflow_name: string,
  description: string,
  assistant_chain: string[],
  model_id: string | undefined,
  is_public: boolean = false
) => {
  try {
    const response = await api.post("/workflows/", {
      workflow_name,
      description,
      assistant_chain,
      model_id,
      is_public,
    });
    console.log("Workflow created:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating workflow:", error);
    throw error;
  }
};

export const updateWorkflowById = async (
  workflow_id: string,
  updatedData: Partial<{
    workflow_name: string;
    description: string;
    assistant_chain: string[];
    model_id: string;
    is_public: boolean;
  }>
) => {
  try {
    const response = await api.put(`/workflows/${workflow_id}`, updatedData);
    console.log("Workflow updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating workflow:", error);
    throw error;
  }
};

export const deleteWorkflowById = async (workflow_id: string) => {
  if (!workflow_id) {
    console.error("Workflow ID is required");
    return;
  }

  try {
    const response = await api.delete(`/workflows/${workflow_id}`);
    console.log("Workflow deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting workflow:", error);
    throw error;
  }
};

export const getWorkflowById = async (workflow_id: string) => {
  try {
    const response = await api.get(`/workflows/${workflow_id}`);
    console.log("Workflow details:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching workflow:", error);
    throw error;
  }
};

export const loadWorkflows = async (
  page: number = 1,
  items_per_page: number = 10,
  view_type: string = 'my_workflows'
) => {
  try {
    const response = await api.get(`/workflows/`, {
      params: {
        page,
        items_per_page,
        view_type,
      },
    });
    console.log("Workflows loaded:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error loading workflows:", error);
    throw error;
  }
};

export const validateWorkflowChain = async (
  assistant_chain: string[]
) => {
  try {
    const response = await api.post("/workflows/validate", { assistant_chain });
    console.log("Workflow validation result:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error validating workflow:", error);
    throw error;
  }
};

// Workflow Favorites API calls
export const addWorkflowToFavorites = async (
  workflow_id: string,
  is_favorite: boolean
) => {
  try {
    const response = await api.post(`/workflows/${workflow_id}/favorite`, {
      is_favorite
    });
    console.log("Workflow favorite status updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating workflow favorite status:", error);
    throw error;
  }
};

export const getUserFavoriteWorkflows = async (
  page: number = 1,
  items_per_page: number = 10
) => {
  try {
    const response = await api.get(`/workflows/favorites`, {
      params: {
        page,
        items_per_page
      }
    });
    console.log("User favorite workflows loaded:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error loading user favorite workflows:", error);
    throw error;
  }
};

export const removeWorkflowFromFavorites = async (workflow_id: string) => {
  try {
    const response = await api.delete(`/workflows/${workflow_id}/favorite`);
    console.log("Workflow removed from favorites:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing workflow from favorites:", error);
    throw error;
  }
};

// Get notifications for current user
export const getNotifications = async (
  page: number = 1,
  items_per_page: number = 10,
  status_filter?: 'unread' | 'read'
): Promise<NotificationResponse> => {
  try {
    const params: any = { page, items_per_page };
    if (status_filter) {
      params.status_filter = status_filter;
    }

    const response = await api.get("/notifications/", { params });
    console.log("Notifications retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving notifications:", error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadNotificationCount = async (): Promise<UnreadCountResponse> => {
  try {
    const response = await api.get("/notifications/unread-count");
    console.log("Unread notification count retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving unread notification count:", error);
    throw error;
  }
};

// Mark notification as read
export const markNotificationAsRead = async (created_at: string): Promise<NotificationActionResponse> => {
  try {
    const response = await api.patch(`/notifications/${encodeURIComponent(created_at)}/read`);
    console.log("Notification marked as read:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<NotificationActionResponse> => {
  try {
    const response = await api.patch("/notifications/mark-all-read");
    console.log("All notifications marked as read:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

// Delete notification
export const deleteNotification = async (created_at: string): Promise<NotificationActionResponse> => {
  try {
    const response = await api.delete(`/notifications/${encodeURIComponent(created_at)}`);
    console.log("Notification deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting notification:", error);
    throw error;
  }
};

// DynamoDB attribute value type
interface DynamoDBValue {
  S?: string;
  BOOL?: boolean;
}

// interface to handle DynamoDB format
export interface VAPermissions {
  share_with_everyone: boolean | DynamoDBValue | any;
  assign_to_users: boolean | DynamoDBValue | any;
  assign_to_department: boolean | DynamoDBValue | any;
  change_model: boolean | DynamoDBValue | any;
  // Optional: absent from APIs that predate them, where creating is allowed.
  create_va?: boolean;
  create_brainbank?: boolean;
}

export interface VAPermissionsResponse {
  success: boolean;
  permissions: VAPermissions;
}

export interface VAPermissionsUpdateResponse {
  success: boolean;
  permissions: VAPermissions;
  message: string;
}


// Get current VA sharing permissions (Admin only)
export const getVAPermissions = async (): Promise<VAPermissionsResponse> => {
  try {
    const response = await api.get("/app-settings/va-permissions");
    console.log("VA permissions retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving VA permissions:", error);
    throw error;
  }
};

// Update VA sharing permissions (Admin only)
export const updateVAPermissions = async (
  permissions: VAPermissions
): Promise<VAPermissionsUpdateResponse> => {
  try {
    const response = await api.put("/app-settings/va-permissions", permissions);
    console.log("VA permissions updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating VA permissions:", error);
    throw error;
  }
};

// Initialize VA permissions with defaults (Admin only) 
export const initializeVAPermissions = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await api.post("/app-settings/va-permissions/initialize");
    console.log("VA permissions initialized:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error initializing VA permissions:", error);
    throw error;
  }
};

export const checkVAPermissionForDepartment = async (): Promise<boolean> => {
  try {
    const response = await api.get("/app-settings/va-permissions/check/assign_to_department");
    return response.data.has_permission === true;
  } catch (error) {
    console.error('❌ Error checking department permission:', error);
    return false;
  }
};

export const checkVAPermissionShareWithUsers = async (): Promise<boolean> => {
  try {
    const response = await api.get("/app-settings/va-permissions/check/share_with_everyone");
    return response.data.has_permission === true;
  } catch (error) {
    console.error('❌ Error checking share permission:', error);
    return false;
  }
};

export const checkVAPermissionAssignManagers = async (): Promise<boolean> => {
  try {
    const response = await api.get("/app-settings/va-permissions/check/assign_to_users");
    return response.data.has_permission === true;
  } catch (error) {
    console.error('❌ Error checking assign managers permission:', error);
    return false;
  }
};

export const checkVAPermissionChangeModel = async (): Promise<boolean> => {
  try {
    const response = await api.get("/app-settings/va-permissions/check/change_model");
    return response.data.has_permission === true;
  } catch (error) {
    console.error('❌ Error checking change model permission:', error);
    return false;
  }
};

// Unlike the checks above, these fail open: creating was always allowed, and
// the API enforces the setting itself, so a failed check must not hide the
// Create buttons for everyone.
export const checkVAPermissionCreateVA = async (): Promise<boolean> => {
  try {
    const response = await api.get("/app-settings/va-permissions/check/create_va");
    return response.data.has_permission !== false;
  } catch (error) {
    console.error('❌ Error checking create VA permission:', error);
    return true;
  }
};

export const checkVAPermissionCreateBrainBank = async (): Promise<boolean> => {
  try {
    const response = await api.get("/app-settings/va-permissions/check/create_brainbank");
    return response.data.has_permission !== false;
  } catch (error) {
    console.error('❌ Error checking create BrainBank permission:', error);
    return true;
  }
};

// ============================================================================
// Meeting Session API calls
// ============================================================================

// Create a new meeting session
export const createMeetingSession = async (
  title?: string,
  description?: string
): Promise<CreateMeetingSessionResponse> => {
  try {
    const response = await api.post('/meeting-session/', { title, description });
    console.log('Meeting session created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating meeting session:', error);
    throw error;
  }
};

// Get paginated list of meeting sessions
export const getMeetingSessions = async (
  page: number = 1,
  itemsPerPage: number = 10
): Promise<MeetingSessionListResponse> => {
  try {
    const response = await api.get('/meeting-session/', {
      params: { page, items_per_page: itemsPerPage }
    });
    console.log('Meeting sessions retrieved:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error retrieving meeting sessions:', error);
    throw error;
  }
};

// Get a specific meeting session by ID
export const getMeetingSessionById = async (
  sessionId: string
): Promise<MeetingSession> => {
  try {
    const response = await api.get(`/meeting-session/${sessionId}`);
    console.log('Meeting session details:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error retrieving meeting session:', error);
    throw error;
  }
};

// Update meeting session title/description
export const updateMeetingSession = async (
  sessionId: string,
  updates: { title?: string; description?: string }
): Promise<MeetingSession> => {
  try {
    const response = await api.put(`/meeting-session/${sessionId}`, updates);
    console.log('Meeting session updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating meeting session:', error);
    throw error;
  }
};

// Delete a meeting session
export const deleteMeetingSession = async (
  sessionId: string
): Promise<{ id: string; message: string }> => {
  try {
    const response = await api.delete(`/meeting-session/${sessionId}`);
    console.log('Meeting session deleted:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error deleting meeting session:', error);
    throw error;
  }
};

// Get raw live transcript segments (reconnect/reload backfill, WS-01)
export const getLiveTranscript = async (
  sessionId: string
): Promise<{ segments: any[]; meta: Record<string, any> }> => {
  const response = await api.get(`/meeting-session/${sessionId}/live-transcript`);
  return response.data;
};

// ---------------------------------------------------------------------------
// BrainBank export (WS-02)
//
// The transcript is rendered server-side, so these calls carry ids, never
// clinical content.
// ---------------------------------------------------------------------------

// Every BrainBank this session's transcript has been sent to. Identity only —
// each copy's processing state comes from getFile(file_id).
export const listMeetingBrainBankExports = async (
  sessionId: string
): Promise<BrainBankExport[]> => {
  const response = await api.get(`/meeting-session/${sessionId}/brain-bank-exports`);
  return response.data?.exports ?? [];
};

// Send the transcript to one BrainBank. `clientRequestId` is minted once per
// dialog and reused on retry: a repeat returns the first result instead of
// sending a second copy. A 409 carries the existing copy so the caller can
// offer Replace.
export const exportMeetingTranscriptToBrainBank = async (
  sessionId: string,
  knowledgeBaseId: string,
  clientRequestId: string,
  replace = false
): Promise<BrainBankExportResult> => {
  const response = await api.post(
    `/meeting-session/${sessionId}/brain-bank-export`,
    {
      knowledge_base_id: knowledgeBaseId,
      client_request_id: clientRequestId,
      replace,
    }
  );
  return response.data;
};

// Take a copy back out of a BrainBank. The session transcript is untouched.
export const removeMeetingBrainBankExport = async (
  sessionId: string,
  fileId: string
): Promise<{ message: string; file_deleted: boolean }> => {
  const response = await api.delete(
    `/meeting-session/${sessionId}/brain-bank-export/${fileId}`
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Chat attachments to BrainBank
//
// The file is already uploaded to the conversation; the server copies it into
// the BrainBank, so these calls carry names and ids, never file content.
// ---------------------------------------------------------------------------

// Add one sent attachment to a BrainBank. `fileName` is the stored name from
// meta.files. `clientRequestId` makes a double submit or retry add one copy.
export const syncSessionFileToBrainBank = async (
  sessionId: string,
  fileName: string,
  knowledgeBaseId: string,
  clientRequestId: string
): Promise<ChatBrainBankSyncResult> => {
  const response = await api.post(`/session-file/brain-bank-sync`, {
    session_id: sessionId,
    file_name: fileName,
    knowledge_base_id: knowledgeBaseId,
    client_request_id: clientRequestId,
  });
  return response.data;
};

// The caller's attachments in this conversation that are in a BrainBank.
export const listSessionBrainBankSyncs = async (
  sessionId: string
): Promise<SessionFileBrainBankSyncs[]> => {
  const response = await api.get(`/session-file/brain-bank-syncs/${sessionId}`);
  return response.data?.files ?? [];
};

// The rendered transcript document — the same bytes a BrainBank indexes.
// Returned as a blob so the download writes exactly what the server produced.
export const getMeetingTranscriptDocument = async (
  sessionId: string
): Promise<Blob> => {
  const response = await api.get(
    `/meeting-session/${sessionId}/transcript-document`,
    { responseType: 'blob' }
  );
  return response.data as Blob;
};

// ---------------------------------------------------------------------------
// WS-03 — imported transcript (PDF backup for the live transcript)
// ---------------------------------------------------------------------------

// Import one PDF as the session's imported transcript. One per meeting —
// importing again replaces it. The server re-syncs every BrainBank copy.
export const importMeetingTranscriptPdf = async (
  sessionId: string,
  file: File
): Promise<{
  message: string;
  imported: ImportedTranscriptMeta;
  replaced_previous: boolean;
  brain_bank_sync: BrainBankSyncSummary;
}> => {
  const formData = new FormData();
  formData.append("pdf", file);
  const response = await api.post(
    `/meeting-session/${sessionId}/imported-transcript`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
};

// The imported transcript with its full text, exactly as extracted from the
// PDF. 404 means the meeting has none — the tab's empty state.
export const getMeetingImportedTranscript = async (
  sessionId: string
): Promise<ImportedTranscript> => {
  const response = await api.get(
    `/meeting-session/${sessionId}/imported-transcript`
  );
  return response.data;
};

// A presigned URL for the original PDF the transcript came from.
export const getMeetingImportedPdfDownloadUrl = async (
  sessionId: string
): Promise<string> => {
  const response = await api.get(
    `/meeting-session/${sessionId}/imported-transcript/download-url`
  );
  return response.data?.presigned_url;
};

// Delete the imported transcript from the meeting AND, automatically, from
// every BrainBank it was sent to. The live transcript is untouched.
export const deleteMeetingImportedTranscript = async (
  sessionId: string
): Promise<{ message: string; brain_bank_sync: BrainBankSyncSummary }> => {
  const response = await api.delete(
    `/meeting-session/${sessionId}/imported-transcript`
  );
  return response.data;
};

// Delete ONLY the session's audio, keeping the transcript (WS-01 / D17)
export const deleteMeetingAudio = async (
  sessionId: string
): Promise<{ message: string }> => {
  try {
    const response = await api.delete(`/meeting-session/${sessionId}/audio`);
    return response.data;
  } catch (error) {
    console.error('Error deleting meeting audio:', error);
    throw error;
  }
};

// Upload an audio chunk for a meeting session
export const uploadMeetingChunk = async (
  sessionId: string,
  chunkIndex: number,
  audioBlob: Blob
): Promise<ChunkUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('audio', audioBlob, `chunk_${chunkIndex}.wav`);

    const response = await api.post(
      `/meeting-session/${sessionId}/chunk/${chunkIndex}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    console.log('Meeting chunk uploaded:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error uploading meeting chunk:', error);
    throw error;
  }
};

// Get upload status for a meeting session
export const getMeetingUploadStatus = async (
  sessionId: string
): Promise<UploadStatusResponse> => {
  try {
    const response = await api.get(`/meeting-session/${sessionId}/upload-status`);
    console.log('Meeting upload status:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting meeting upload status:', error);
    throw error;
  }
};

// Finalize a meeting session (triggers transcription)
export const finalizeMeetingSession = async (
  sessionId: string,
  totalChunks: number,
  durationSeconds: number
): Promise<FinalizeResponse> => {
  try {
    const response = await api.post(`/meeting-session/${sessionId}/finalize`, {
      total_chunks: totalChunks,
      duration_seconds: durationSeconds
    });
    console.log('Meeting session finalized:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error finalizing meeting session:', error);
    throw error;
  }
};

// Get presigned download URL for meeting audio
export const getMeetingDownloadUrl = async (
  sessionId: string
): Promise<{ presigned_url: string; expires_in: number }> => {
  try {
    const response = await api.get(`/meeting-session/${sessionId}/download-url`);
    console.log('Meeting download URL generated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting meeting download URL:', error);
    throw error;
  }
};

// Get transcription status for a meeting session
export const getTranscriptionStatus = async (
  sessionId: string
): Promise<TranscriptionStatusResponse> => {
  try {
    const response = await api.get(`/meeting-session/${sessionId}/transcription-status`);
    console.log('Transcription status:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting transcription status:', error);
    throw error;
  }
};

// Get transcription content for a meeting session
export const getTranscription = async (
  sessionId: string
): Promise<Transcription> => {
  try {
    const response = await api.get(`/meeting-session/${sessionId}/transcription`);
    console.log('Transcription retrieved:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting transcription:', error);
    throw error;
  }
};

// Update speaker mapping for a meeting session
export const updateSpeakerMapping = async (
  sessionId: string,
  mappings: SpeakerMapping[]
): Promise<{ message: string }> => {
  try {
    const response = await api.put(`/meeting-session/${sessionId}/speaker-mapping`, {
      mappings
    });
    console.log('Speaker mapping updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating speaker mapping:', error);
    throw error;
  }
};

// Retry transcription for a stuck/failed meeting session
export const retryMeetingTranscription = async (
  sessionId: string,
  regenerate: boolean = false
): Promise<{ message: string; transcription_job_id?: string }> => {
  try {
    const response = await api.post(
      `/meeting-session/${sessionId}/retry`,
      null,
      regenerate ? { params: { regenerate: true } } : undefined
    );
    console.log('Meeting transcription retry started:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error retrying meeting transcription:', error);
    throw error;
  }
};

// PDF Generation
export const generatePdfFromHtml = async (
  htmlContent: string,
  fileName: string
): Promise<Blob> => {
  try {
    const response = await api.post(
      "/document-export/generate-pdf",
      { html_content: htmlContent, file_name: fileName },
      { responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

// Excel Monitor Schedule
export interface ScheduleStatus {
  schedule_name: string;
  is_enabled: boolean;
  interval_value: number;
  interval_unit: string;
  schedule_expression: string;
  timezone: string;
  last_modified: string | null;
}

export interface UpdateScheduleResponse {
  message: string;
  schedule_name: string;
  schedule_expression: string;
  interval_value: number;
  interval_unit: string;
  is_enabled: boolean;
}

export const getExcelMonitorSchedule =
  async (): Promise<ScheduleStatus> => {
    try {
      const response = await api.get("/excel-monitor/schedule");
      console.log(
        "Excel monitor schedule retrieved:",
        response.data
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error retrieving excel monitor schedule:",
        error
      );
      throw error;
    }
  };

export const updateExcelMonitorSchedule = async (
  interval_value: number,
  interval_unit: "minutes" | "hours" | "days"
): Promise<UpdateScheduleResponse> => {
  try {
    const response = await api.put("/excel-monitor/schedule", {
      interval_value,
      interval_unit,
    });
    console.log(
      "Excel monitor schedule updated:",
      response.data
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating excel monitor schedule:",
      error
    );
    throw error;
  }
};

// Excel Monitor Email Config
export interface EmailConfigStatus {
  ses_sender_email: string;
  ses_recipient_email: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface UpdateEmailConfigRequest {
  ses_sender_email?: string;
  ses_recipient_email?: string;
}

export interface UpdateEmailConfigResponse {
  message: string;
  ses_sender_email: string;
  ses_recipient_email: string;
  updated_at: string | null;
}

export const getExcelMonitorEmailConfig =
  async (): Promise<EmailConfigStatus> => {
    try {
      const response = await api.get("/excel-monitor/email-config");
      console.log(
        "Excel monitor email config retrieved:",
        response.data
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error retrieving excel monitor email config:",
        error
      );
      throw error;
    }
  };

export const updateExcelMonitorEmailConfig = async (
  data: UpdateEmailConfigRequest
): Promise<UpdateEmailConfigResponse> => {
  try {
    const response = await api.put(
      "/excel-monitor/email-config",
      data
    );
    console.log(
      "Excel monitor email config updated:",
      response.data
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating excel monitor email config:",
      error
    );
    throw error;
  }
};

// Excel Monitor File Config
export interface FileConfigStatus {
  google_drive_file_id: string;
  file_name: string;
  file_mime_type: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface UpdateFileConfigResponse {
  message: string;
  google_drive_file_id: string;
  file_name: string;
  file_mime_type: string;
  updated_at: string | null;
}

export const getExcelMonitorFileConfig =
  async (): Promise<FileConfigStatus> => {
    try {
      const response = await api.get("/excel-monitor/file-config");
      return response.data;
    } catch (error) {
      console.error("Error fetching excel monitor file config:", error);
      throw error;
    }
  };

export const updateExcelMonitorFileConfig = async (
  google_drive_file_id: string,
  file_name: string,
  file_mime_type: string
): Promise<UpdateFileConfigResponse> => {
  try {
    const response = await api.put("/excel-monitor/file-config", {
      google_drive_file_id,
      file_name,
      file_mime_type,
    });
    console.log("Excel monitor file config updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating excel monitor file config:", error);
    throw error;
  }
};

export const deleteExcelMonitorFileConfig = async (): Promise<{ message: string }> => {
  try {
    const response = await api.delete("/excel-monitor/file-config");
    console.log("Excel monitor file config removed:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing excel monitor file config:", error);
    throw error;
  }
};

// ── Multi-file monitor APIs ──────────────────────────────────────

export interface MonitorFileItem {
  google_drive_file_id: string;
  file_name: string;
  file_mime_type: string;
  connected_user_id?: string;
  updated_at?: string;
  updated_by?: string;
}

export const listMonitorFiles = async (): Promise<{ files: MonitorFileItem[] }> => {
  try {
    const response = await api.get("/excel-monitor/files");
    return response.data;
  } catch (error) {
    console.error("Error listing monitor files:", error);
    throw error;
  }
};

export const addMonitorFile = async (
  google_drive_file_id: string,
  file_name: string,
  file_mime_type: string
): Promise<MonitorFileItem & { message: string }> => {
  try {
    const response = await api.post("/excel-monitor/files", {
      google_drive_file_id,
      file_name,
      file_mime_type,
    });
    return response.data;
  } catch (error) {
    console.error("Error adding monitor file:", error);
    throw error;
  }
};

export const removeMonitorFile = async (fileId: string): Promise<{ message: string }> => {
  try {
    const response = await api.delete(`/excel-monitor/files/${fileId}`);
    return response.data;
  } catch (error) {
    console.error("Error removing monitor file:", error);
    throw error;
  }
};

// ── Toggle schedule (enable/disable) ─────────────────────────────

export interface ToggleScheduleResponse {
  message: string;
  schedule_name: string;
  is_enabled: boolean;
  interval_value: number;
  interval_unit: string;
  schedule_expression: string;
  timezone: string;
}

export const toggleExcelMonitorSchedule = async (
  enabled: boolean
): Promise<ToggleScheduleResponse> => {
  try {
    const response = await api.put("/excel-monitor/schedule/toggle", {
      enabled,
    });
    console.log("Excel monitor schedule toggled:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error toggling excel monitor schedule:", error);
    throw error;
  }
};

// ── Run history ──────────────────────────────────────────────────

export interface RunHistoryItem {
  file_id: string;
  file_name: string;
  last_checked: string | null;
  last_hash: string | null;
  last_revision_id: string | null;
  status: string | null;
  change_detected: boolean;
  error_message: string | null;
}

export interface RunHistoryResponse {
  runs: RunHistoryItem[];
}

export const getExcelMonitorRunHistory = async (): Promise<RunHistoryResponse> => {
  try {
    const response = await api.get("/excel-monitor/run-history");
    console.log("Excel monitor run history retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving excel monitor run history:", error);
    throw error;
  }
};

// ══════════════════════════════════════════════════════════════════
// Email Monitor API
// ══════════════════════════════════════════════════════════════════

// ── Email Monitor Schedule ──

export interface EmailMonitorScheduleStatus {
  schedule_name: string;
  is_enabled: boolean;
  interval_value: number;
  interval_unit: string;
  schedule_expression: string;
  timezone: string;
  last_modified: string | null;
}

export interface UpdateEmailMonitorScheduleResponse {
  message: string;
  schedule_name: string;
  schedule_expression: string;
  interval_value: number;
  interval_unit: string;
  is_enabled: boolean;
}

export const getEmailMonitorSchedule =
  async (): Promise<EmailMonitorScheduleStatus> => {
    try {
      const response = await api.get("/email-monitor/schedule");
      console.log("Email monitor schedule retrieved:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error retrieving email monitor schedule:", error);
      throw error;
    }
  };

export const updateEmailMonitorSchedule = async (
  interval_value: number,
  interval_unit: "minutes" | "hours" | "days"
): Promise<UpdateEmailMonitorScheduleResponse> => {
  try {
    const response = await api.put("/email-monitor/schedule", {
      interval_value,
      interval_unit,
    });
    console.log("Email monitor schedule updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating email monitor schedule:", error);
    throw error;
  }
};

export interface ToggleEmailMonitorScheduleResponse {
  message: string;
  schedule_name: string;
  is_enabled: boolean;
  interval_value: number;
  interval_unit: string;
  schedule_expression: string;
  timezone: string;
}

export const toggleEmailMonitorSchedule = async (
  enabled: boolean
): Promise<ToggleEmailMonitorScheduleResponse> => {
  try {
    const response = await api.put("/email-monitor/schedule/toggle", {
      enabled,
    });
    console.log("Email monitor schedule toggled:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error toggling email monitor schedule:", error);
    throw error;
  }
};

// ── Email Monitor Config (CRUD) ──

export interface EmailMonitorItem {
  email_address: string;
  connected_user_id?: string;
  updated_at?: string;
  updated_by?: string;
}

export const listEmailMonitors = async (): Promise<{ emails: EmailMonitorItem[] }> => {
  try {
    const response = await api.get("/email-monitor/emails");
    console.log("Email monitors retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving email monitors:", error);
    throw error;
  }
};

export const addEmailMonitor = async (
  email_address: string
): Promise<EmailMonitorItem & { message: string }> => {
  try {
    const response = await api.post("/email-monitor/emails", {
      email_address,
    });
    console.log("Email monitor added:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding email monitor:", error);
    throw error;
  }
};

export const removeEmailMonitor = async (emailAddress: string): Promise<void> => {
  try {
    const response = await api.delete(`/email-monitor/emails/${encodeURIComponent(emailAddress)}`);
    console.log("Email monitor removed:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing email monitor:", error);
    throw error;
  }
};

// ── Email Monitor Notification Config ──

export interface EmailNotificationConfigStatus {
  ses_sender_email: string;
  ses_recipient_email: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface UpdateEmailNotificationConfigRequest {
  ses_sender_email?: string;
  ses_recipient_email?: string;
}

export interface UpdateEmailNotificationConfigResponse {
  message: string;
  ses_sender_email: string;
  ses_recipient_email: string;
  updated_at: string | null;
}

export const getEmailMonitorNotificationConfig =
  async (): Promise<EmailNotificationConfigStatus> => {
    try {
      const response = await api.get("/email-monitor/notification-config");
      console.log("Email notification config retrieved:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error retrieving email notification config:", error);
      throw error;
    }
  };

export const updateEmailMonitorNotificationConfig = async (
  data: UpdateEmailNotificationConfigRequest
): Promise<UpdateEmailNotificationConfigResponse> => {
  try {
    const response = await api.put("/email-monitor/notification-config", data);
    console.log("Email notification config updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating email notification config:", error);
    throw error;
  }
};

// ── Email Monitor Run History ──

export interface EmailMonitorRunHistoryItem {
  email_address: string;
  last_checked: string | null;
  new_emails_count: number;
  status: string | null;
  error_message: string | null;
}

export interface EmailMonitorRunHistoryResponse {
  runs: EmailMonitorRunHistoryItem[];
}

export const getEmailMonitorRunHistory = async (): Promise<EmailMonitorRunHistoryResponse> => {
  try {
    const response = await api.get("/email-monitor/run-history");
    console.log("Email monitor run history retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving email monitor run history:", error);
    throw error;
  }
};

// ══════════════════════════════════════════════════════════════════
// Outlook Monitor API
// ══════════════════════════════════════════════════════════════════

// ── Outlook Monitor Schedule ──

export interface OutlookMonitorScheduleStatus {
  schedule_name: string;
  is_enabled: boolean;
  interval_value: number;
  interval_unit: string;
  schedule_expression: string;
  timezone: string;
  last_modified: string | null;
}

export interface UpdateOutlookMonitorScheduleResponse {
  message: string;
  schedule_name: string;
  schedule_expression: string;
  interval_value: number;
  interval_unit: string;
  is_enabled: boolean;
}

export const getOutlookMonitorSchedule =
  async (): Promise<OutlookMonitorScheduleStatus> => {
    try {
      const response = await api.get("/outlook-monitor/schedule");
      console.log("Outlook monitor schedule retrieved:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error retrieving outlook monitor schedule:", error);
      throw error;
    }
  };

export const updateOutlookMonitorSchedule = async (
  interval_value: number,
  interval_unit: "minutes" | "hours" | "days"
): Promise<UpdateOutlookMonitorScheduleResponse> => {
  try {
    const response = await api.put("/outlook-monitor/schedule", {
      interval_value,
      interval_unit,
    });
    console.log("Outlook monitor schedule updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating outlook monitor schedule:", error);
    throw error;
  }
};

export interface ToggleOutlookMonitorScheduleResponse {
  message: string;
  schedule_name: string;
  is_enabled: boolean;
  interval_value: number;
  interval_unit: string;
  schedule_expression: string;
  timezone: string;
}

export const toggleOutlookMonitorSchedule = async (
  enabled: boolean
): Promise<ToggleOutlookMonitorScheduleResponse> => {
  try {
    const response = await api.put("/outlook-monitor/schedule/toggle", {
      enabled,
    });
    console.log("Outlook monitor schedule toggled:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error toggling outlook monitor schedule:", error);
    throw error;
  }
};

// ── Outlook Monitor Config (CRUD) ──

export interface OutlookMonitorItem {
  email_address: string;
  connected_user_id?: string;
  updated_at?: string;
  updated_by?: string;
}

export const listOutlookMonitors = async (): Promise<{ emails: OutlookMonitorItem[] }> => {
  try {
    const response = await api.get("/outlook-monitor/emails");
    console.log("Outlook monitors retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving outlook monitors:", error);
    throw error;
  }
};

export const addOutlookMonitor = async (
  email_address: string
): Promise<OutlookMonitorItem & { message: string }> => {
  try {
    const response = await api.post("/outlook-monitor/emails", {
      email_address,
    });
    console.log("Outlook monitor added:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding outlook monitor:", error);
    throw error;
  }
};

export const removeOutlookMonitor = async (emailAddress: string): Promise<void> => {
  try {
    const response = await api.delete(`/outlook-monitor/emails/${encodeURIComponent(emailAddress)}`);
    console.log("Outlook monitor removed:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error removing outlook monitor:", error);
    throw error;
  }
};

// ── Outlook Monitor Notification Config ──

export interface OutlookNotificationConfigStatus {
  ses_sender_email: string;
  ses_recipient_email: string;
  updated_at: string | null;
  updated_by: string | null;
}

export interface UpdateOutlookNotificationConfigRequest {
  ses_sender_email?: string;
  ses_recipient_email?: string;
}

export interface UpdateOutlookNotificationConfigResponse {
  message: string;
  ses_sender_email: string;
  ses_recipient_email: string;
  updated_at: string | null;
}

export const getOutlookMonitorNotificationConfig =
  async (): Promise<OutlookNotificationConfigStatus> => {
    try {
      const response = await api.get("/outlook-monitor/notification-config");
      console.log("Outlook notification config retrieved:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error retrieving outlook notification config:", error);
      throw error;
    }
  };

export const updateOutlookMonitorNotificationConfig = async (
  data: UpdateOutlookNotificationConfigRequest
): Promise<UpdateOutlookNotificationConfigResponse> => {
  try {
    const response = await api.put("/outlook-monitor/notification-config", data);
    console.log("Outlook notification config updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating outlook notification config:", error);
    throw error;
  }
};

// ── Outlook Monitor Run History ──

export interface OutlookMonitorRunHistoryItem {
  email_address: string;
  last_checked: string | null;
  new_emails_count: number;
  status: string | null;
  error_message: string | null;
}

export interface OutlookMonitorRunHistoryResponse {
  runs: OutlookMonitorRunHistoryItem[];
}

export const getOutlookMonitorRunHistory = async (): Promise<OutlookMonitorRunHistoryResponse> => {
  try {
    const response = await api.get("/outlook-monitor/run-history");
    console.log("Outlook monitor run history retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error retrieving outlook monitor run history:", error);
    throw error;
  }
};

// ══════════════════════════════════════════════════════════════════
// Scheduled Hat Execution (POST /hat-admin/schedule)
// Independent from excel-monitor / email-monitor flows.
// Backend uses "Hat" terminology to match the rest of kyva-api.
// ══════════════════════════════════════════════════════════════════

export type HatScheduleType = "interval" | "cron";
export type HatScheduleUnit = "minutes" | "hours" | "days";

export type HatSkill = "drive-monitor" | "outlook-monitor";

export interface HatDriveMonitorTaskConfig {
  google_drive_file_id: string;
  file_name: string;
  file_mime_type?: string;
  // Per-Hat recipient. Sender is hardcoded server-side to info@kyva.ai.
  recipient_email: string;
}

export interface HatOutlookMonitorTaskConfig {
  // Per-Hat recipient for the previous-working-day email digest.
  // Sender is hardcoded server-side to info@kyva.ai.
  recipient_email: string;
  folder?: string; // default "Inbox"
}

export type HatTaskConfig =
  | HatDriveMonitorTaskConfig
  | HatOutlookMonitorTaskConfig;

export interface HatIntervalSchedule {
  interval_value: number;
  interval_unit: HatScheduleUnit;
}

export interface HatCronSchedule {
  expression: string;
}

export interface HatRetryPolicy {
  enabled: boolean;
  max_count: number;
  backoff_sec: number;
}

// IANA names, so EventBridge follows daylight saving (EST/EDT) on its own.
export type HatScheduleTimezone = "UTC" | "America/New_York";

export interface CreateHatScheduleRequest {
  hat_id: string;
  hat_name?: string;
  skill: HatSkill;
  task_config: HatTaskConfig;
  schedule_type: HatScheduleType;
  interval?: HatIntervalSchedule;
  cron?: HatCronSchedule;
  retry?: HatRetryPolicy;
  timezone?: HatScheduleTimezone;
}

export interface CreateHatScheduleResponse {
  message: string;
  hat_id: string;
  schedule_name: string;
  schedule_expression: string;
  schedule_arn?: string;
  enabled: boolean;
}

export const createHatSchedule = async (
  payload: CreateHatScheduleRequest
): Promise<CreateHatScheduleResponse> => {
  const response = await api.post("/hat-admin/schedule", payload);
  return response.data;
};

export interface HatScheduleRow {
  va_id: string;                          // DynamoDB attribute kept as `va_id` for legacy continuity
  schedule_type: HatScheduleType;
  schedule_value: string;
  timezone?: HatScheduleTimezone;         // absent on rows saved before time zones existed = UTC
  enabled: boolean;
  retry_enabled: boolean;
  retry_max_count: number;
  retry_backoff_sec: number;
  eventbridge_schedule_arn?: string;
  updated_at?: string;
  // Joined from va-def# row by the backend (best-effort)
  skill?: string;
  task_config?: {
    google_drive_file_id?: string;
    file_name?: string;
    file_mime_type?: string;
    connected_user_id?: string;
    recipient_email?: string;
    folder?: string;
  };
}

export interface GetHatScheduleResponse {
  data: HatScheduleRow | null;
}

export const getHatSchedule = async (
  hatId: string
): Promise<GetHatScheduleResponse> => {
  const response = await api.get(`/hat-admin/schedule/${encodeURIComponent(hatId)}`);
  return response.data;
};

export interface ToggleHatScheduleResponse {
  message: string;
  hat_id: string;
  enabled: boolean;
}

export const toggleHatSchedule = async (
  hatId: string,
  enabled: boolean
): Promise<ToggleHatScheduleResponse> => {
  const response = await api.patch(
    `/hat-admin/schedule/${encodeURIComponent(hatId)}/state`,
    { enabled }
  );
  return response.data;
};

export interface DeleteHatScheduleResponse {
  message: string;
  hat_id: string;
}

export const deleteHatSchedule = async (
  hatId: string
): Promise<DeleteHatScheduleResponse> => {
  const response = await api.delete(`/hat-admin/schedule/${encodeURIComponent(hatId)}`);
  return response.data;
};

// ── Schedule cost estimate ─────────────────────────────────────────
// Read-only preview used by the VA form: how often would this schedule
// run, and what would that cost? Creates nothing. Costs come back null
// when the run frequency can't be derived from the expression, so the UI
// must handle nulls rather than defaulting them to 0.

export interface ScheduleCostLineItem {
  label: string;
  monthly_cost: number;
  detail: string;
}

/** A less-frequent interval the user can switch to in one tap. */
export interface ScheduleCostAlternative {
  label: string;
  interval_value: number;
  interval_unit: HatScheduleUnit;
  monthly_cost: number;
}

export interface ScheduleCostEstimateRequest {
  schedule_type: HatScheduleType;
  interval?: HatIntervalSchedule;
  cron?: HatCronSchedule;
  skill?: HatSkill;
  hat_id?: string;
}

export interface ScheduleCostEstimate {
  schedule_expression: string;
  runs_per_day: number | null;
  runs_per_month: number | null;
  per_run_cost: number | null;
  monthly_cost: number | null;
  currency: string;
  billable_seconds_per_run: number | null;
  /** Fargate task size actually priced, read from the live ECS task definition. */
  task_vcpu: number | null;
  task_memory_gb: number | null;
  /** Where the task size came from — "default" means the ECS lookup was unavailable. */
  task_size_source: "task-definition" | "service" | "default";
  /** e.g. "openclaw-monitor-task:16"; empty when not read from ECS. */
  task_definition: string;
  /** One plain sentence covering what the estimate includes and excludes. */
  summary: string;
  basis: string;
  basis_source: "observed" | "default";
  sample_size: number;
  alternatives: ScheduleCostAlternative[];
  /** Itemised form of `summary`; the VA form shows `summary` instead. */
  line_items: ScheduleCostLineItem[];
  warnings: string[];
  assumptions: string[];
}

export const estimateScheduleCost = async (
  payload: ScheduleCostEstimateRequest
): Promise<ScheduleCostEstimate> => {
  const response = await api.post("/hat-admin/schedule/cost-estimate", payload);
  return response.data;
};

// ── Hat Run History ────────────────────────────────────────────────
// Records expire after the retention window enforced by DynamoDB TTL.
// `retention_days` is returned by every read so the UI can surface it.

export type HatRunOutcome = "success" | "failure" | "no_action";

export interface HatRunRecord {
  hat_id: string;
  run_id: string;
  started_at: string;
  finished_at?: string | null;
  duration_ms?: number | null;
  outcome: HatRunOutcome;
  error_message?: string | null;
  attempt: number;
  details?: Record<string, unknown> | null;
}

export interface HatRunHistoryResponse {
  runs: HatRunRecord[];
  retention_days: number;
  count: number;
  next_token?: string | null;
}

// ── Cross-VA run history (admin overview) ──────────────────────────
// Used by the admin Run History dashboard to show runs across all VAs by
// default. Pass `hatId` to narrow to a single VA. The single-VA and
// "latest run" backend routes (/hat-admin/schedule/{hat_id}/runs[/latest])
// remain available server-side but no UI currently calls them.

export const getAllHatRuns = async (
  opts: {
    hatId?: string;
    from?: string;
    to?: string;
  } = {}
): Promise<HatRunHistoryResponse> => {
  try {
    const params: any = {};
    if (opts.hatId) params.hat_id = opts.hatId;
    if (opts.from) params.from = opts.from;
    if (opts.to) params.to = opts.to;
    const response = await api.get("/hat-admin/runs", { params });
    return response.data;
  } catch (error) {
    console.error("Error retrieving cross-VA run history:", error);
    throw error;
  }
};

// ── List Hats that have a schedule (for the run-history filter) ────
// Returns only VAs with a va-schedule#<id> row in openclaw-secrets, joined
// with `hat_name` from the va-def#<id> row when available.

export interface ScheduledHatSummary {
  va_id: string;
  hat_name?: string;
  schedule_type?: HatScheduleType;
  schedule_value?: string;
  enabled?: boolean;
  task_config?: {
    google_drive_file_id?: string;
    file_name?: string;
    file_mime_type?: string;
    connected_user_id?: string;
    recipient_email?: string;
  };
}

export const listHatSchedules = async (): Promise<ScheduledHatSummary[]> => {
  try {
    const response = await api.get("/hat-admin/schedules");
    return response.data?.data ?? [];
  } catch (error) {
    console.error("Error listing hat schedules:", error);
    throw error;
  }
};

// ── Image Studio ───────────────────────────────────────────────────────────
// Self-contained client for the Stability-on-Bedrock image module. Mirrors the
// backend /image-studio router. Reference uploads reuse uploadFileToS3 above.

export interface StudioToolParam {
  name: string;
  type: "string" | "text" | "int" | "float" | "enum" | "bool";
  required: boolean;
  default?: any;
  min?: number | null;
  max?: number | null;
  enum?: string[] | null;
  description?: string | null;
}

export interface StudioToolSpec {
  key: string;
  label: string;
  mode: "stage" | "generate" | "edit" | "control" | "style" | "upscale";
  needs_image: boolean;
  optional_image?: boolean;
  needs_mask?: boolean;
  needs_style_image?: boolean;
  params: StudioToolParam[];
}

// ── Presets (guided wizards) ──
export interface PresetCondition {
  field: string;
  equals: string;
}

export interface PresetFieldOption {
  value: string;
  label: string;
  summary?: string;
  help?: string;
}

export interface PresetField {
  type: "image" | "segmented" | "select" | "chips" | "cards" | "slider";
  name: string;
  label: string;
  options?: PresetFieldOption[];
  suggestions?: string[];
  required?: boolean;
  default?: any;
  min?: number;
  max?: number;
  step?: number;
  as?: string | null;
  help?: string | null;
  show_when?: PresetCondition | null;
  slot?: string;
}

export interface StudioPreset {
  key: string;
  label: string;
  description: string;
  icon?: string;
  needs_image?: boolean;
  needs_mask_when?: PresetCondition | null;
  fields: PresetField[];
}

export interface StudioGallery {
  gallery_id: string;
  name: string;
  asset_count: number;
  cover_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface StudioGenerateRequest {
  tool?: string | null;
  preset?: string | null;
  gallery_id: string;
  params: Record<string, any>;
  reference_key?: string | null;
  parent_asset_id?: string | null;
  mask_b64?: string | null;
  style_reference_key?: string | null;
  variants?: number;
}

export interface StudioGenerateResponse {
  asset_id: string;
  gallery_id: string;
  presigned_url: string;
  s3_key: string;
  tool: string;
  model_id: string;
  mode: string;
  output_format: string;
  seed?: number | null;
  finish_reason?: string | null;
  created_at?: string | null;
}

export interface StudioAsset {
  asset_id: string;
  gallery_id: string;
  asset_type: "generated" | "uploaded";
  presigned_url: string;
  s3_key: string;
  output_format: string;
  file_name?: string | null;
  tool?: string | null;
  model_id?: string | null;
  mode?: string | null;
  prompt?: string | null;
  params: Record<string, any>;
  seed?: number | null;
  parent_asset_id?: string | null;
  created_at?: string | null;
}

export interface StudioUploadResult {
  assetId: string;
  s3Key: string;
}

export const imageStudioService = {
  async getTools(): Promise<StudioToolSpec[]> {
    const response = await api.get("/image-studio/tools");
    return response.data?.data ?? [];
  },

  async getPresets(): Promise<StudioPreset[]> {
    const response = await api.get("/image-studio/presets");
    return response.data?.data ?? [];
  },

  // Galleries
  async listGalleries(): Promise<StudioGallery[]> {
    const response = await api.get("/image-studio/galleries");
    return response.data?.data ?? [];
  },

  async createGallery(name?: string): Promise<StudioGallery> {
    const response = await api.post("/image-studio/galleries", { name });
    return response.data;
  },

  async renameGallery(galleryId: string, name: string): Promise<void> {
    await api.patch(`/image-studio/galleries/${galleryId}`, { name });
  },

  async deleteGallery(galleryId: string): Promise<void> {
    await api.delete(`/image-studio/galleries/${galleryId}`);
  },

  // Generation + assets. The endpoint returns one entry per requested variant.
  async generate(payload: StudioGenerateRequest): Promise<StudioGenerateResponse[]> {
    const response = await api.post("/image-studio/generate", payload);
    return response.data?.data ?? [];
  },

  async getAssets(galleryId: string): Promise<StudioAsset[]> {
    const response = await api.get(`/image-studio/galleries/${galleryId}/assets`);
    return response.data?.data ?? [];
  },

  async deleteAsset(galleryId: string, assetId: string): Promise<void> {
    await api.delete(`/image-studio/galleries/${galleryId}/assets/${assetId}`);
  },

  // Upload a reference image: creates an 'uploaded' asset row + presigned PUT.
  // content_type is sent so the signed URL's Content-Type matches the PUT.
  async uploadReference(
    galleryId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<StudioUploadResult> {
    const response = await api.post("/image-studio/upload-url", {
      gallery_id: galleryId,
      file_name: file.name,
      content_type: file.type,
    });
    const { presigned_url, s3_key, asset_id } = response.data;
    await uploadFileToS3(presigned_url, file, onProgress);
    return { assetId: asset_id, s3Key: s3_key };
  },
};

export const scanGoogleDriveFileOnly = async (
  google_drive_file_id: string,
  scan_enabled?: boolean
) => {
  try {
    const payload: any = { google_drive_file_id };
    if (scan_enabled !== undefined) {
      payload.scan_enabled = scan_enabled;
    }

    const response = await api.post("/knowledge-base/google-drive/scan-file-only", payload);
    return response.data;
  } catch (error) {
    console.error("Error with Google Drive scan file only:", error);
    throw error;
  }
};

export const uploadDirectGoogleDriveFile = async (
  google_drive_file_id: string,
  knowledge_base_id: string,
  index_name: string,
  processing_mode: string = "auto",
  file_hash?: string,
  scan_enabled?: boolean,
  scan_id?: string
) => {
  try {
    const payload: any = {
      google_drive_file_id,
      knowledge_base_id,
      index_name,
      processing_mode,
    };
    if (file_hash) payload.file_hash = file_hash;
    if (scan_enabled !== undefined) payload.scan_enabled = scan_enabled;
    if (scan_id) payload.scan_id = scan_id;

    const response = await api.post("/knowledge-base/google-drive/upload-direct", payload);
    return response.data;
  } catch (error) {
    console.error("Error with direct Google Drive file upload:", error);
    throw error;
  }
};

// ==========================================
// DROPBOX INTEGRATION
// ==========================================

export const getDropboxAuthUrl = async (): Promise<string> => {
  try {
    const response = await api.get('/dropbox/auth/url');
    return response.data.auth_url;
  } catch (error) {
    console.error('Error getting Dropbox auth URL:', error);
    throw error;
  }
};

export const exchangeDropboxAuthCode = async (code: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.post('/dropbox/auth/callback', { code });
    return response.data;
  } catch (error: any) {
    console.error('Error exchanging Dropbox auth code:', error);
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export const checkDropboxConnection = async (): Promise<boolean> => {
  try {
    const response = await api.get('/dropbox/auth/status');
    return response.data.connected || false;
  } catch (error) {
    console.error('Error checking Dropbox connection:', error);
    return false;
  }
};

export const disconnectDropbox = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.post('/dropbox/auth/disconnect');
    return response.data;
  } catch (error: any) {
    console.error('Error disconnecting Dropbox:', error);
    return { success: false, error: error.response?.data?.detail || error.message };
  }
};

export interface DropboxFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  createdTime?: string;
}

export const listDropboxFiles = async (
  pageToken?: string,
  query?: string,
  pageSize: number = 50,
  path?: string
): Promise<{ success: boolean; files: DropboxFile[]; next_page_token?: string; error?: string }> => {
  try {
    const response = await api.get('/dropbox/files', {
      params: { path: path || pageToken || '' },
    });
    return { success: true, ...response.data };
  } catch (error: any) {
    console.error('Error listing Dropbox files:', error);
    return { success: false, files: [], error: error.message };
  }
};

export const checkDropboxConfigured = async (): Promise<boolean> => {
  try {
    const response = await api.get('/dropbox/auth/config');
    return response.data.configured || false;
  } catch (error) {
    console.error('Error checking Dropbox configuration:', error);
    return false;
  }
};

export const uploadDirectDropboxFile = async (
  dropboxFileId: string,
  brainbankId: string = "unassigned",
  department: string = "unassigned",
  processingMode: "standard" | "high_accuracy" | "fast" = "standard",
  fileHash: string,
  scanEnabled: boolean = false,
  scanId?: string
) => {
  try {
    const response = await api.post('/dropbox/upload-direct', {
      dropbox_file_id: dropboxFileId,
      brainbank_id: brainbankId,
      department,
      processing_mode: processingMode,
      file_hash: fileHash,
      scan_enabled: scanEnabled,
      scan_id: scanId
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading Dropbox file:', error);
    throw error;
  }
};

export const scanDropboxFileOnly = async (dropboxFileId: string, scanEnabled: boolean = true) => {
  try {
    const response = await api.post('/dropbox/scan-file-only', {
      dropbox_file_id: dropboxFileId,
      scan_enabled: scanEnabled
    });
    return response.data;
  } catch (error) {
    console.error('Error scanning Dropbox file:', error);
    throw error;
  }
};
